/**
 * La FORME d'un brief : ce qu'on cree, ce qu'on sauve, ce qu'on relit.
 *
 * Porte de V1 (`Bible MyTaki/launcher/index.html`) : `blank()` devient
 * `nouveauBrief()`, `newPorte()` devient `nouvellePorte()`, `newChamp()` devient
 * `nouveauChamp()`, et les rustines de `load()` deviennent `migrer()`.
 *
 * Les cles ne se renomment PAS, meme les obscures (`tranche`, `vb`, `ftAuto`,
 * `labelMode`) : un brief sauve sur disque par V1 doit rester lisible ici, et un
 * brief ecrit ici doit rester lisible par un humain. Une cle qui change de SENS,
 * elle, se paie dans `migrer` et fait monter `VERSION_MODELE` : c'est ce qui est
 * arrive a `dsRef` (l'uid d'une porte, puis l'uid d'un jeu).
 *
 * Ce module tourne dans Node natif et dans le navigateur. Il importe seulement
 * les identifiants purs du socle. `uid()` est sa seule impurete.
 */

import {
  FAMILLE_A_DECIDER,
  clefDeChamp,
  estFamilleADecider,
  familleADeciderDe,
  kebab,
} from '../../identifiants.mjs';
export { FAMILLE_A_DECIDER, clefDeChamp, estFamilleADecider, familleADeciderDe };

/**
 * La version de la forme d'un brief. Elle monte quand une cle change de sens ou
 * de nom, jamais quand on ajoute une cle facultative.
 *
 * 2 : LE JEU DE DONNEES DEVIENT UNE ENTITE A PART. Il vivait DANS une porte
 * (`porte.ds`), et `dsRef` designait « l'uid de la PORTE reprise » : un jeu
 * n'avait donc pas d'identite, il etait identifie par la porte qui se trouvait
 * le posseder. Il vit maintenant a la racine (`brief.jeux[]`), avec son propre
 * `uid`, et `dsRef` designe l'uid d'un JEU. La cle change de REFERENT sans
 * changer de nom : c'est exactement le cas ou la version monte.
 *
 * 3 : LES QUATRE DERNIERES CLEFS CESSENT DE STOCKER DU FRANCAIS. `filtres`,
 * `favoris` et `tri` portaient des PHRASES (« ce qu'il garde », « filtres et
 * tri enregistres », « Nom croissant, puis date ») la ou le socle attend des
 * REFERENCES : un uid de champ, une valeur de catalogue, un sens. Rien ne
 * pouvait s'en deriver, donc une app neuve naissait sans filtre, sans favori et
 * sans bouton Ajouter. Les trois changent de FORME, `actions` gagne son nom
 * d'appel DERIVE (rien de plus a taper), et la doctrine qui l'explique vit dans
 * `docs/CHANTIERS-SOCLE.md`.
 *
 * 4 : UN FAVORI DESIGNE SES FILTRES PAR UNE REFERENCE, plus par leur libelle.
 * La version 3 avait laisse passer exactement ce qu'elle supprimait : un favori
 * stockait « Camera », c'est-a-dire le MOT affiche a l'ecran. Deux degats, tous
 * deux silencieux : retoucher le libelle d'une option faisait DISPARAITRE le
 * favori (la projection ne resolvait plus, et ecartait sans rien dire), et un
 * filtre nomme homonyme d'une valeur du catalogue ECRASAIT cette valeur (un
 * champ « Etat » a choix « Actif, En panne » plus un filtre nomme « En panne »
 * donnait un favori qui posait le predicat au lieu de l'egalite cochee). Un
 * favori porte desormais `valeur:<valeur stockee>` ou `filtre:<uid>` : deux
 * espaces de noms qui ne peuvent plus se cogner.
 *
 * 8 : UNE CARTE DE FICHE A UNE DISPOSITION, ET ELLE EN A TROIS. Le booleen
 * `wide` ne savait dire que deux etats (dans une colonne, ou la rangee
 * entiere), alors que le socle en rendait DEJA trois : une carte large a UN
 * champ etale ce champ sur toute la largeur (la carte Notes), une carte large a
 * PLUSIEURS champs met son TITRE sur la rangee et repartit ses champs sur les
 * deux colonnes. Le troisieme etat existait donc dans le produit sans exister
 * dans le vocabulaire, et personne ne pouvait le demander. `wide` devient
 * `disposition`, une valeur parmi `demi`, `pleine` et `titre-pleine` ; la clef
 * change de NOM et de TYPE, c'est exactement le cas ou la version monte.
 *
 * 9 : UNE VALEUR FIGEE SE COCHE TELLE QU'ELLE EST STOCKEE. Aucune clef ne
 * change, mais une DONNEE deja sauvee etait fausse, comme en 4 et en 6 : le
 * formulaire cochait `valeur:fournisseur` la ou `clefsValeurs` stocke
 * `supplier`, et le favori se projetait sans filtre. La migration retraduit ce
 * qui ne resout pas, et seulement quand un seul libelle le designe.
 * @type {number}
 */
export const VERSION_MODELE = 9;

/* ========================================================================== */
/* les references d'un favori                                                 */
/* ========================================================================== */

/**
 * CE QU'UN FAVORI COCHE : une REFERENCE, jamais un libelle.
 *
 * DEUX ESPACES DE NOMS, ET C'EST TOUT LE POINT. Les filtres d'une porte
 * viennent de deux sources qui ne se ressemblent pas : les VALEURS du champ a
 * choix (le socle en tire une egalite par valeur) et les filtres NOMMES (un
 * predicat que l'agent ecrit). Tant que la reference etait le libelle affiche,
 * les deux sources partageaient un seul espace de noms, et un homonyme ecrasait
 * l'autre en silence.
 *
 * UNE VALEUR SE DESIGNE PAR LA VALEUR STOCKEE (`valeur:enPanne`), pas par son
 * libelle : c'est ce que porte le document, ce que le fichier de champs ecrit
 * (`options[].value`) et ce que le socle compare. UN FILTRE NOMME SE DESIGNE PAR
 * SON UID (`filtre:f3k2...`), le jeton opaque que le formulaire lui a donne a sa
 * creation : le renommer ne le fait plus disparaitre.
 */
export const REF_VALEUR = 'valeur:';
/** @see REF_VALEUR */
export const REF_FILTRE = 'filtre:';

/** La reference d'une valeur du catalogue `parChamp`. @param {string} valeur @returns {string} */
export function refValeur(valeur) {
  return REF_VALEUR + String(valeur || '');
}

/** La reference d'un filtre NOMME, par son uid. @param {string} uid @returns {string} */
export function refFiltre(uid) {
  return REF_FILTRE + String(uid || '');
}

/** Cette chaine est-elle deja une reference (et non un libelle) ?
 * @param {unknown} texte @returns {boolean} */
export function estReferenceDeFiltre(texte) {
  const t = String(texte || '');
  return t.startsWith(REF_VALEUR) || t.startsWith(REF_FILTRE);
}

/**
 * Les valeurs d'un champ a choix, telles que le formulaire les saisit : une
 * chaine a virgules. Ici pour la meme raison que `clefDeChamp`, et re-exportee
 * par `derive.mjs`.
 *
 * @param {any} champ @returns {string[]}
 */
export function valeursDeChamp(champ) {
  if (Array.isArray(champ?.valeursListe) && champ.valeursListe.join(', ') === champ.valeurs) {
    return [...champ.valeursListe];
  }
  return String((champ && champ.valeurs) || '')
    .split(',')
    .map((x) => x.trim())
    .filter(Boolean);
}

/* ========================================================================== */
/* uid : la seule impurete du lot, isolee pour qu'un test puisse la remplacer  */
/* ========================================================================== */

/** Le generateur de V1, mot pour mot. @returns {string} */
const uidParDefaut = () => Math.random().toString(36).slice(2, 9);

/** @type {() => string} */
let generateurUid = uidParDefaut;

/**
 * Un identifiant court et stable, pose une fois et jamais recalcule : c'est lui
 * qui designe un champ dans une proposition de relecture, jamais son index.
 * @returns {string}
 */
export function uid() {
  return generateurUid();
}

/**
 * LA POIGNEE D'UN PROJET D'APP : le nom du fichier sous lequel il vit dans
 * `.taki-studio/briefs/`, tiree UNE fois et jamais recalculee.
 *
 * POURQUOI ELLE NE SUIT PAS LE NOM DE L'APP, et c'est tout l'interet : l'id
 * d'un brief SUIT son nom tant qu'on ne l'ecrit pas soi-meme, donc le fichier
 * se renommait a chaque frappe. Le chemin deja colle a un agent devenait faux,
 * chaque frappe laissait un orphelin de plus sur le disque, et deux projets
 * encore sans nom ecrivaient tous les deux dans le meme `brouillon.json`.
 *
 * ELLE N'ENTRE PAS DANS LE BRIEF : c'est une donnee de la couche locale (ou le
 * fichier est range), pas une propriete de l'app decrite. C'est ce qui garde le
 * fichier exportable tel quel, et importable sous une poignee neuve.
 *
 * SA FORME SATISFAIT `ID_BRIEF_RE` du pont (`[A-Za-z0-9][A-Za-z0-9_-]{0,63}`)
 * sans que le serveur ait une regle de plus a connaitre.
 *
 * @returns {string}
 */
export function nouvellePoignee() {
  return `p-${Date.now().toString(36)}-${uid()}`;
}

/**
 * Remplace le generateur d'uid, pour un test (un compteur, par exemple).
 * Passer `null` remet celui de V1.
 * @param {(() => string) | null} generateur
 * @returns {() => void} de quoi remettre le generateur precedent
 */
export function definirGenerateurUid(generateur) {
  const precedent = generateurUid;
  generateurUid = typeof generateur === 'function' ? generateur : uidParDefaut;
  return () => {
    generateurUid = precedent;
  };
}

/* ========================================================================== */
/* les trois niveaux de canvas (regle G23)                                    */
/* ========================================================================== */

/**
 * NATIVE = ce qui SUIT le gabarit. CANVAS = ce qui en SORT. Il existe
 * exactement TROIS niveaux de canvas, nommes en anglais par le fondateur le
 * 24/08/2026 : App Canvas, Space Canvas, View Canvas. Un niveau se definit par
 * les ZONES qu'il reprend au gabarit, rien d'autre. Doctrine, table des zones
 * et regle G23 : `docs/GABARIT-ECRAN.md` section 9.
 *
 * LE FORMULAIRE PORTAIT DEJA LES TROIS NIVEAUX, il ne savait pas les nommer.
 * Les trois clefs existent depuis V1 et ne bougent pas : `appCustom` (App
 * Canvas), `porte.custom` (Space Canvas, l'ancienne « porte custom »),
 * `porte.customVues` + `porte.vuesCustom[]` (View Canvas). Ce bloc leur donne
 * leur nom, les zones qu'un niveau a le DROIT de garder, et les deux clefs qui
 * manquaient pour DECLARER la sortie dans `taki.app.json` (cle `canvas`).
 *
 * VOCABULAIRE, tranche par le fondateur le 24/08/2026 : « Space » est le nom
 * produit de la PORTE, mais le renommage global est un chantier A PART. Le
 * formulaire continue de dire PORTE partout ; « Space Canvas » ne nomme que le
 * NIVEAU. Rien d'autre n'est renomme ici.
 */

/** Les identifiants des zones historiques, pour lire les anciens briefs.
 * Les nouvelles declarations utilisent le catalogue elements du data-model.
 */
export const ZONES_CANVAS = Object.freeze({
  '2b': Object.freeze({ id: '2b', libelle: 'la recherche', interdit: null }),
  '2c': Object.freeze({ id: '2c', libelle: 'les stats', interdit: null }),
  '3a': Object.freeze({ id: '3a', libelle: 'les actions', interdit: null }),
  '3b': Object.freeze({ id: '3b', libelle: 'les outils', interdit: null }),
  '3c': Object.freeze({
    id: '3c',
    libelle: 'le sélecteur de vues',
    interdit: "sans vues du socle, il n'y a rien à sélectionner",
  }),
});

/** Raccourcis historiques : ils ne limitent pas le panneau canonique. */
const ZONES_APP_CANVAS = Object.freeze(['2b', '2c', '3a', '3b']);
const ZONES_SPACE_CANVAS = Object.freeze(['3a', '3b']);

/**
 * La date d'accord du fondateur, `AAAA-MM-JJ`. Meme expression que
 * `CANVAS_DATE_RE` de `scripts/check-app-entities.mjs`, et pour la meme raison :
 * la fiche EXIGE `canvas.grantedOn` des qu'un niveau est declare. Sans elle,
 * l'app naitrait avec une fiche que `pnpm apps:check` refuse.
 */
export const DATE_ACCORD_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * L'id kebab-case que la fiche accepte. Meme expression que `APP_ID_RE` de
 * `scripts/check-app-entities.mjs`, qui la fait respecter sur l'id de l'app ET
 * sur `canvas.spaces[].id` : une porte declaree en Space Canvas avec un id qui
 * ne tient pas cette forme fait refuser la fiche entiere.
 */
export const ID_KEBAB_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/**
 * @typedef {Object} NiveauCanvas
 * @property {'app'|'space'|'view'} id     l'identifiant STABLE, partage entre modules
 * @property {string} nom                  son nom produit, en anglais : « App Canvas »...
 * @property {'brief'|'porte'} ou          ou vit son interrupteur : a la racine, ou sur une porte
 * @property {string} drapeau              la clef de l'interrupteur (`appCustom`, `custom`...)
 * @property {string|null} prose           la clef du champ libre, quand le niveau en a UN seul
 * @property {string|null} clefZones       la clef qui porte les zones gardees
 * @property {readonly string[]} zones     les zones acceptees dans le format historique
 * @property {'app'|'spaces'|'views'} fiche la clef qu'il remplit sous `canvas` de `taki.app.json`
 * @property {string} garde                ce qu'il garde du gabarit, en une phrase
 * @property {boolean} socle               le socle sait-il le faire AUJOURD'HUI
 * @property {string|null} aConstruire     sinon, la phrase qui le dit
 */

/**
 * LE CATALOGUE DES TROIS NIVEAUX, et lui seul fait foi. Quatre modules ecrivent
 * contre lui (`derive.mjs`, `validate.mjs`, `prompt.mjs`, le formulaire) : un
 * nom recopie a la main quelque part est un nom qui divergera.
 *
 * `socle` vaut VRAI sur les trois. Il a valu faux sur deux d'entre eux, parce
 * que la section 9 de `docs/GABARIT-ECRAN.md` disait encore « le socle ne sait
 * faire que le View Canvas » : cette ligne avait ete ecrite AVANT la passe qui a
 * construit les trois niveaux et n'avait pas suivi. Le formulaire l'a recopiee,
 * et son prompt ordonnait a l'agent de s'arreter en BLOCAGE SOCLE devant un
 * socle qui savait faire.
 *
 * Ce que le socle porte vraiment : la prop `canvas` de `ScreenLayout`, la porte
 * canvas de `useScreen`, et `CanvasBoundary`. La porte `ma-semaine` de
 * Timesheet est un Space Canvas qui tourne. `aConstruire` reste dans la forme parce qu'un
 * quatrieme niveau, le jour ou il arrivera, arrivera avant son socle.
 */
export const NIVEAUX_CANVAS = Object.freeze({
  app: Object.freeze({
    id: /** @type {'app'} */ ('app'),
    nom: 'App Canvas',
    ou: /** @type {'brief'} */ ('brief'),
    drapeau: 'appCustom',
    prose: 'appCustomPrompt',
    clefZones: 'appCanvasKeeps',
    zones: ZONES_APP_CANVAS,
    fiche: /** @type {'app'} */ ('app'),
    garde:
      "Choisir d’abord le cadre dans le panneau Cadre, puis construire le contenu. Sans élément gardé, le Canvas est entièrement libre. La surface alimente les composants natifs gardés.",
    socle: true,
    aConstruire: null,
  }),
  space: Object.freeze({
    id: /** @type {'space'} */ ('space'),
    nom: 'Space Canvas',
    ou: /** @type {'porte'} */ ('porte'),
    drapeau: 'custom',
    prose: 'customPrompt',
    clefZones: 'canvasKeeps',
    zones: ZONES_SPACE_CANVAS,
    fiche: /** @type {'spaces'} */ ('spaces'),
    garde:
      "Choisir d’abord le cadre dans le panneau Cadre, puis construire le contenu. Sans élément gardé, le Canvas est entièrement libre. Le moteur ou la surface alimente les composants natifs gardés.",
    socle: true,
    aConstruire: null,
  }),
  view: Object.freeze({
    id: /** @type {'view'} */ ('view'),
    nom: 'View Canvas',
    ou: /** @type {'porte'} */ ('porte'),
    drapeau: 'customVues',
    prose: null,
    clefZones: null,
    zones: /** @type {readonly string[]} */ (Object.freeze([])),
    fiche: /** @type {'views'} */ ('views'),
    garde:
      "Tout reste au gabarit : la vue occupe la zone 5 et rien d'autre. Jamais la vue d'arrivée, jamais seule (`docs/VUES-CONTRAT.md` section 6).",
    socle: true,
    aConstruire: null,
  }),
});

/** Les trois niveaux dans l'ordre de la doctrine : du plus large au plus contenu. */
export const NIVEAUX_CANVAS_LISTE = Object.freeze([
  NIVEAUX_CANVAS.app,
  NIVEAUX_CANVAS.space,
  NIVEAUX_CANVAS.view,
]);

/**
 * Le descripteur d'un niveau, ou `null` si l'identifiant est inconnu.
 * @param {string} id @returns {NiveauCanvas|null}
 */
export function niveauCanvasDe(id) {
  const table = /** @type {Record<string, NiveauCanvas>} */ (
    /** @type {unknown} */ (NIVEAUX_CANVAS)
  );
  return Object.prototype.hasOwnProperty.call(table, id) ? table[id] : null;
}

/**
 * Le descripteur d'une zone, ou `null`. Sert a NOMMER une zone dans une phrase
 * (« 3c, le sélecteur de vues ») sans jamais recopier son libelle.
 * @param {string} id @returns {{ id: string, libelle: string, interdit: string|null }|null}
 */
export function zoneCanvasDe(id) {
  const table = /** @type {Record<string, { id: string, libelle: string, interdit: string|null }>} */ (
    /** @type {unknown} */ (ZONES_CANVAS)
  );
  return Object.prototype.hasOwnProperty.call(table, id) ? table[id] : null;
}

/* ========================================================================== */
/* les constructeurs                                                          */
/* ========================================================================== */

/**
 * @typedef {Object} Champ
 * @property {string} uid
 * @property {string} nom
 * @property {string} clef la clef de STOCKAGE, figee au premier nom commis ; vide tant que le champ n'a pas de nom
 * @property {string} nature une valeur de NATURES (`familles.mjs`)
 * @property {boolean} requis
 * @property {string} exemple LE PLACEHOLDER de la case vide (« ex. : Sony FX9 n°3 »), rendu
 *   dans la fiche de CREATION et dans les editeurs. Vide veut dire « le defaut du socle par
 *   nature » : il ne s'ecrit alors pas dans `taki.fields.json`
 * @property {string} valeurs les choix, separes par des virgules, si nature = liste de choix
 * @property {string} cible `porte:<uid>`, `jeu:<index>`, `libre`, ou vide
 * @property {string} cibleTxt la cible en clair quand `cible` vaut `libre`
 * @property {{ famille: string, condition: object }} [choixReference] LES CHOIX PROPOSES par un champ
 *   lie (`reference.choix` du fichier de champs) : une condition du socle, lue dans le vocabulaire de
 *   la famille visee, gardee avec cette famille. Reprise du fichier, jamais saisie ici ; elle ne
 *   s'ecrit que tant que la cible vise encore `famille`. Absente = tout le jeu
 * @property {string} symbole L'ANCIEN SYMBOLE d'un prix (`€`), que seul le prompt lisait : il n'a
 *   jamais atteint la declaration. La devise d'un prix est desormais `devise` ; ce champ reste
 *   pour qu'un brief d'avant se relise a l'identique, et ne decide plus rien
 * @property {string} [nomPersonne] ce qui nomme une personne de l'organisation a l'ecran
 *   (`socle:displayName` ou `socle:email`), si nature = personne ; vide = le nom, sinon l'e-mail
 * @property {string} [dependDe] l'uid du champ « liste de choix » du meme jeu dont les valeurs
 *   de celui-ci dependent (`dependDe` de `taki.fields.json`) ; vide = aucun
 * @property {Record<string, string>} [parentsValeurs] la valeur STOCKEE du parent de chaque
 *   valeur, libelle par libelle ; absente = la valeur vaut pour tous les parents
 * @property {string} [unite] L'UNITE affichee apres le nombre (« m² »), si nature = nombre ou calcul
 * @property {string} [decimales] le nombre de decimales montrees, en texte (`''` = le defaut), si
 *   nature = nombre ou calcul
 * @property {string} [devise] le code ISO 4217 (`USD`), si nature = prix ou calcul ; vide = EUR pour
 *   un prix (son habillage `euros`, ecrit sur le champ), aucune devise pour un calcul
 * @property {string} [affichage] `jauge` : un pourcentage montre en barre reglable (0 a 100), si
 *   nature = pourcentage ; absent = en chiffres
 * @property {boolean} [plusieurs] VRAI : le champ lie vise plusieurs fiches (une liste d'ids), si
 *   nature = champ lié ; absent = une seule fiche
 * @property {string} [max] la note maximale, en texte (`''` = 5), si nature = note
 * @property {string} [formule] la formule d'un calcul, en forme STOCKEE (`{prix} * 0.25`) quand elle
 *   se lit contre les champs du jeu, telle que tapee sinon (le refus la montre)
 * @property {string} [modele] le modele d'un numero automatique (`ASS-{année}-{numéro}`) ; vide = le
 *   numero nu
 * @property {string} [chiffres] le nombre de chiffres d'un numero automatique, en texte (`''` = aucun
 *   remplissage)
 *
 * LES REGLAGES D'UN TYPE (les neuf dernieres clefs) sont FACULTATIFS, et leur
 * absence vaut le defaut du socle : un brief ecrit avant le 27/10/2026 se lit
 * exactement comme avant, et `nouveauChamp` ne les pose pas. Ils sont en TEXTE
 * parce qu'ils se saisissent dans une case ; c'est `reglagesDeclares`
 * (`reglages-de-champ.mjs`) qui les ramene a la forme du fichier.
 */

/**
 * Un champ tout neuf.
 * @returns {Champ}
 */
export function nouveauChamp() {
  return { uid: uid(), nom: '', clef: '', natif: '', nature: 'texte', requis: false, exemple: '', valeurs: '', clefsValeurs: {}, tonsValeurs: {}, habillage: '', cible: '', cibleTxt: '', symbole: '€' };
}

/**
 * LA VALEUR STOCKEE D'UNE OPTION, figee quand l'app en porte deja une autre.
 *
 * MEME RAISON QUE `clefDuChamp`, UN CRAN PLUS BAS. Le formulaire saisit des
 * LIBELLES (« A definir ») et la valeur stockee s'en derive (`aDefinir`) : une
 * app dont l'option vaut `a-definir` sur le disque et dans ses fiches verrait
 * donc sa valeur changer a la premiere synchronisation, et toutes les fiches
 * deja saisies perdraient leur valeur a l'ecran.
 *
 * `clefsValeurs` GARDE CE QUE L'APP PORTE DEJA, libelle par libelle. Absent ou
 * sans entree, la valeur se derive du libelle comme avant : un brief ecrit
 * avant cette clef se lit exactement pareil. Renommer un libelle perd son
 * entree, donc rederive la valeur : c'est le comportement d'aujourd'hui, on ne
 * l'aggrave pas.
 *
 * C'EST LA SEULE SOURCE DE LA VALEUR D'UNE OPTION, et pas seulement pour le
 * fichier de champs : la case qu'un favori coche (`filtresOfferts`), la valeur
 * d'une stat au formulaire, la relecture et les migrations la lisent aussi. Un
 * `clefDeChamp(libelle)` pose a cote d'elle diverge des qu'une valeur est
 * figee : l'Annuaire (14/09/2026) a eu un favori sans filtre, en silence.
 *
 * @param {{ clefsValeurs?: Record<string, string> } | null | undefined} champ
 * @param {string} libelle @returns {string}
 */
/**
 * LE PARENT D'UNE VALEUR (la valeur stockee du champ dont celui-ci depend,
 * `dependDe`), libelle par libelle, ou `''` : la valeur vaut pour tous.
 *
 * @param {{ parentsValeurs?: Record<string, string> } | null | undefined} champ
 * @param {string} libelle @returns {string}
 */
export function parentDeValeur(champ, libelle) {
  const table = champ?.parentsValeurs;
  const parent = table && typeof table === 'object' ? table[libelle] : undefined;
  return typeof parent === 'string' ? parent : '';
}

/**
 * LE TON D'UNE VALEUR DE CATALOGUE, libelle par libelle, ou `''`.
 *
 * Le ton est le seul vocabulaire de couleur partage par les apps (une clef
 * de `TONS`, ou un hex garde tel quel, `packages/data-model/src/couleurs.ts`) :
 * c'est lui qui fait qu'une pastille « En retard » se lit rouge. Il ne se devine pas d'un libelle, et le formulaire ne le demandait
 * nulle part : une app nee du Studio n'avait donc que des pastilles grises.
 *
 * `''` se lit « neutre », comme une entree absente : un brief ecrit avant cette
 * clef rend exactement ce qu'il rendait.
 *
 * @param {{ tonsValeurs?: Record<string, string> } | null | undefined} champ
 * @param {string} libelle @returns {string}
 */
export function tonDeValeur(champ, libelle) {
  const table = champ?.tonsValeurs;
  const ton = table && typeof table === 'object' ? table[libelle] : undefined;
  return estTon(ton) ? ton : '';
}

/**
 * Les clefs de la grille du design (`COULEURS_DU_DESIGN`,
 * `packages/data-model/src/couleurs.ts`), dans son ordre : les quatre tons
 * d'avant en tete. Recopiees parce que ce module tourne en Node natif sans le
 * socle compile ; `packages/shell/tests/tons-du-brief.test.mjs` les tient
 * accordees.
 */
export const TONS = Object.freeze([
  'neutral', 'accent', 'success', 'danger', 'orange', 'amber', 'yellow', 'olive', 'emerald', 'teal',
  'cyan', 'sky', 'blue', 'indigo', 'violet', 'plum', 'pink', 'coral', 'brown', 'slate',
]);

/** Une couleur d'option : une clef de `TONS`, ou un hex `#rgb` / `#rrggbb` garde tel quel. */
export function estTon(ton) {
  return typeof ton === 'string' && (TONS.includes(ton) || /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i.test(ton));
}

export function valeurStockee(champ, libelle) {
  const table = champ?.clefsValeurs;
  const figee = table && typeof table === 'object' ? table[libelle] : undefined;
  return typeof figee === 'string' && figee !== '' ? figee : clefDeChamp(libelle);
}

/**
 * LE LIBELLE D'UNE VALEUR STOCKEE, l'inverse de `valeurStockee`, ou la valeur
 * telle quelle quand aucune option ne la porte.
 *
 * CE QUI SE LIT PAR UN HUMAIN DIT LE LIBELLE. La phrase d'une stat et le libelle
 * propose pour elle lisaient la valeur brute : tant qu'elle se derivait du
 * libelle, on lisait « encours » au lieu de « en cours » ; des qu'elle est figee,
 * on lisait `supplier` dans une app en francais.
 *
 * @param {{ valeurs?: string, clefsValeurs?: Record<string, string> } | null | undefined} champ
 * @param {unknown} valeur @returns {string}
 */
export function libelleDeValeur(champ, valeur) {
  const brut = String(valeur ?? '');
  return valeursDeChamp(champ).find((l) => valeurStockee(champ, l) === brut) ?? brut;
}

/**
 * LES DEUX NATURES DONT LES VALEURS SONT UN CATALOGUE D'OPTIONS, chacune avec
 * son libelle et sa valeur stockee. Ce qui offre, verifie ou migre une valeur
 * d'option vaut pour les deux : n'en citer qu'une a laisse la liste multiple
 * hors du correctif de `studio-valeur-stockee`, a sa premiere relecture.
 */
export const NATURES_A_CHOIX = Object.freeze(['liste de choix', 'liste de choix multiple']);

/**
 * LA NATURE QUI VISE UNE FICHE D'UNE AUTRE FAMILLE : `champ lié`, le `lien` du
 * socle, un id ou, coche « Plusieurs fiches » (`plusieurs`), une liste d'ids.
 * Sa cible se choisit par `cible`, `cibleTxt` et `titreReference`. Une liste,
 * et pas une egalite nue, parce que ce qui juge, choisit ou reprend une cible
 * la lit ici : une seconde nature liee s'ajoute en une entree (la faute que
 * `NATURES_A_CHOIX` raconte juste au-dessus).
 */
export const NATURES_LIEES = Object.freeze(['champ lié']);

/** Vrai quand la nature vise une fiche d'une autre famille ({@link NATURES_LIEES}). @param {unknown} nature */
export const estNatureLiee = (nature) => NATURES_LIEES.includes(/** @type {string} */ (nature));

/**
 * LE CHAMP NATIF DE LA FAMILLE QUE CE CHAMP NOMME, ou `''`.
 *
 * `natif` VIDE EST LE CAS COURANT : le champ vit dans `customFields`, il
 * s'ecrit dans `taki.fields.json`. Non vide (`name`, `reference`, `basePrice`,
 * `startAt`), le champ nomme une colonne que la FAMILLE porte deja : il ne
 * s'ecrit dans aucun fichier de champs, mais les vues le citent
 * (`socle:<clef>`).
 *
 * POURQUOI IL EXISTE. Sans lui, un titre pose sur le nom de la famille n'avait
 * AUCUN mot dans le brief : la reprise d'une app finie a la main laissait la
 * case vide et le formulaire reprochait « aucun champ ne fait le titre » a une
 * app qui marche. Clef FACULTATIVE, absente = vide : un brief ecrit avant elle
 * se lit exactement comme avant, et aucun golden ne bouge.
 *
 * @param {{ natif?: string } | null | undefined} champ @returns {string}
 */
export function natifDuChamp(champ) {
  return String(champ?.natif ?? '').trim();
}

/**
 * LA CLEF D'UN CHAMP : la figee (`clef`) si elle existe, sinon derivee du nom.
 *
 * POURQUOI FIGER. La clef est celle du STOCKAGE (`customFields.<clef>` de chaque
 * fiche) : renommer un champ apres construction ne doit pas la changer, sinon
 * les fiches existantes perdent leur valeur a l'ecran. Le 4 septembre,
 * « Capacité » renommee « Capacité (places) » avait fait passer la clef de
 * `capacite` a `capacitePlaces` dans taki.fields.json.
 *
 * @param {{ nom?: string, clef?: string } | null | undefined} champ @returns {string}
 */
export function clefDuChamp(champ) {
  const figee = String(champ?.clef ?? '').trim();
  return figee !== '' ? figee : clefDeChamp(champ?.nom);
}

/**
 * FIGE LES CLEFS QUI NE LE SONT PAS ENCORE : tout champ nomme sans `clef` recoit
 * la clef derivee de son nom courant. Idempotent, en place (le brief est deja
 * une copie quand `migrer` l'appelle). Un brief d'avant cette regle garde donc
 * exactement les clefs qu'il ecrivait deja.
 *
 * @param {any} brief @returns {any} le meme brief
 */
export function figerLesClefs(brief) {
  for (const jeu of Array.isArray(brief?.jeux) ? brief.jeux : []) {
    for (const champ of Array.isArray(jeu?.champs) ? jeu.champs : []) {
      if (!champ || typeof champ !== 'object') continue;
      /* UN CHAMP NATIF N'A PAS DE CLEF DE STOCKAGE : il nomme une colonne que la
         famille porte deja, il n'entre dans aucun sac. */
      if (natifDuChamp(champ) !== '') continue;
      if (String(champ.clef ?? '').trim() !== '') continue;
      const clef = clefDeChamp(champ.nom);
      if (clef !== '') champ.clef = clef;
    }
  }
  return brief;
}

/* ========================================================================== */
/* les rangees des quatre clefs structurees (filtres, favoris, tri)           */
/* ========================================================================== */

/**
 * LES CINQ ROLES QUE LE SOCLE SAIT TRIER SANS COLONNE, table FERMEE, et le mot
 * par defaut de chacun.
 *
 * L'AUTORITE EST `clefDeTriSchema` (`packages/data-model/src/app-screens.ts`) :
 * cette copie existe pour la meme raison technique que `FAMILLE_A_DECIDER`, ce
 * dossier tournant dans le navigateur ET sous Node nu. Le MOT, lui, n'existe
 * nulle part ailleurs : le schema l'EXIGE sur un role (« rien d'autre ne
 * connait le mot de "Nom" ou d'"Etat" »), donc le formulaire le pose et le
 * fondateur le retouche.
 *
 * @type {ReadonlyArray<{ id: string, mot: string }>}
 */
export const ROLES_TRI = Object.freeze([
  Object.freeze({ id: 'title', mot: 'Nom' }),
  Object.freeze({ id: 'subtitle', mot: 'Sous-titre' }),
  Object.freeze({ id: 'status', mot: 'État' }),
  Object.freeze({ id: 'dateStart', mot: 'Date de début' }),
  Object.freeze({ id: 'dateEnd', mot: 'Date de fin' }),
]);

/** Les deux sens d'un niveau de tri. Le socle compare, le brief NOMME. */
export const SENS_TRI = Object.freeze([
  Object.freeze({ id: 'asc', mot: 'croissant' }),
  Object.freeze({ id: 'desc', mot: 'décroissant' }),
]);

/**
 * UN FILTRE QUI N'EST PAS « CE CHAMP VAUT CETTE VALEUR ».
 *
 * `garde` reste de la PROSE, et c'est deliberé : ce filtre est un predicat
 * TypeScript que l'agent ecrira, donc la phrase est ce qu'on lui transmet. Son
 * NOM D'APPEL, lui, se derive du libelle : il ne se tape pas.
 *
 * `fn` N'EST PAS UNE DECISION, C'EST UN CONSTAT : il ne se remplit que par la
 * REPRISE, quand le fichier d'ecrans de l'app nomme deja le predicat, donc
 * quand le code de l'app le porte deja. Il etait perdu a l'aller-retour, et la
 * perte coutait un faux verrou : Patisserie, dont les trois predicats sont
 * ecrits depuis le premier jour, restait « son predicat reste a ecrire ».
 * Vide, et c'est le cas de tout filtre neuf, le predicat reste a ecrire et le
 * nom d'appel se derive du libelle.
 * @returns {Object}
 */
export function nouveauFiltreAutre() {
  return { uid: uid(), label: '', garde: '', fn: '' };
}

/**
 * UN FAVORI PRESET : un etat {filtres, pile de tri} NOMME.
 *
 * `filtres` porte des REFERENCES (`valeur:<valeur stockee>`, `filtre:<uid>`),
 * jamais les libelles coches. Il a porte les libelles pendant une version, et
 * les deux degats etaient silencieux : renommer une option faisait disparaitre
 * le favori, un filtre nomme homonyme d'une valeur ecrasait cette valeur. Voir
 * `REF_VALEUR` pour le pourquoi des deux espaces de noms.
 * @returns {Object}
 */
export function nouveauFavori() {
  return { uid: uid(), label: '', filtres: [], tri: [] };
}

/**
 * UN NIVEAU DE LA PILE DE TRI : une clef, un sens.
 *
 * `champ` OU `role`, jamais les deux : c'est le schema du socle qui le refuse,
 * et le formulaire ne propose qu'un selecteur pour les deux.
 * @returns {Object}
 */
export function nouveauNiveauTri() {
  return { uid: uid(), champ: '', role: '', mot: '', sens: 'asc' };
}

/**
 * UNE ACTION D'ECRAN, UNE STAT, UNE VUE SUR MESURE : les trois dernieres
 * rangees qui n'avaient pas de fabrique.
 *
 * ELLES ETAIENT ECRITES EN DUR DANS LEUR SECTION (`brief-ui/SectionActions.tsx`,
 * `brief-ui/SectionStats.tsx`, `brief-ui/SectionDetailParVue.tsx`), un litteral
 * par bouton « + ». Tant que le formulaire etait seul a en creer, ca tenait.
 * Depuis que la RELECTURE peut AJOUTER une rangee (`revue.mjs`, verbe
 * `ajouter`), il y aurait DEUX facons de fabriquer la meme ligne, et c'est la
 * seconde qui divergerait : un agent poserait une clef que le formulaire ne
 * connait pas, ou en oublierait une que le reste du modele deref sans filet.
 *
 * La fabrique est donc la MEME des deux cotes, et c'est elle qui pose l'`uid` :
 * un agent ne devine jamais un uid, il decrit une ligne.
 */

/**
 * @returns {Object} une action d'ecran, vide.
 *
 * `fn` N'EST PAS UNE DECISION, C'EST UN CONSTAT, exactement comme celui d'un
 * filtre nomme (`nouveauFiltreAutre`) : il ne se remplit que par la REPRISE,
 * quand le fichier d'ecrans de l'app nomme deja l'effet, donc quand le code de
 * l'app le porte. Il etait perdu a l'aller-retour, et la perte se payait d'un
 * faux verrou (« ne dit pas ce qu'elle fait ») que le validateur cachait en
 * bloc des que l'app etait construite : le remede taisait alors AUSSI l'action
 * qu'on venait d'ajouter et dont l'effet n'etait ecrit nulle part.
 * Vide, et c'est le cas de toute action neuve, l'effet reste a ecrire et le nom
 * d'appel se derive du libelle. Pour un SCRIPT, le Studio le fige a la
 * premiere saisie du libelle (`figerAction`) : un renommage ne le change plus.
 */
export function nouvelleAction() {
  return {
    uid: uid(),
    /* L'ID D'UN SMART BOUTON EST STABLE (`docs/socle/smart-boutons.md`) : un flux
       s'y abonne. Vide a la naissance, il se pose depuis le libelle UNE fois
       (`figerAction`), puis la reprise le garde tel que le fichier le porte. */
    id: '',
    label: '',
    quoi: '',
    /* LE DECLENCHEMENT : `flux` (le clic lance les flux branches, rien a coder)
       ou `script` (un effet de l'app, que l'agent code). Une action d'un brief
       d'avant cette clef est un script (`declenchementDAction`). */
    declenchement: 'flux',
    /* OU LE BOUTON SE RENDRA : `fiche` (la rangee de la fiche ouverte, la seule
       qui porte une condition) ou `collection` (la rangee 3a). */
    niveau: 'fiche',
    fn: '',
    /* '' EST LE DEFAUT DU SOCLE (« secondaire (défaut) ») : une action d'un
       brief ecrit avant cette clef se lit donc exactement pareil,
       `String(a.variant ?? '')`. */
    variant: '',
    /* LA CONDITION DE DISPONIBILITE (niveau fiche seulement), brute comme celle
       d'un verrou d'en-tete ; `null` : toujours disponible. `siFaux` dit quoi
       faire quand elle est fausse, `raison` remplace la phrase derivee. */
    si: null,
    siFaux: 'griser',
    raison: '',
    champs: [],
    confirm: false,
    open: true,
  };
}

/**
 * LE DECLENCHEMENT D'UNE ACTION DU BRIEF. Seul `flux` se dit : tout le reste est
 * un script, ce qui garde lisibles les briefs d'avant la clef (une action avec
 * `fn`, `fenetre` ou rien du tout, dont l'effet se derivait du libelle).
 *
 * @param {any} action @returns {'flux'|'script'}
 */
export function declenchementDAction(action) {
  return action && action.declenchement === 'flux' ? 'flux' : 'script';
}

/**
 * LE NIVEAU D'UNE ACTION DU BRIEF : `fiche` ou `collection` (le defaut du socle,
 * qu'un brief d'avant le niveau dit par une chaine vide).
 *
 * @param {any} action @returns {'fiche'|'collection'}
 */
export function niveauDAction(action) {
  return action && action.niveau === 'fiche' ? 'fiche' : 'collection';
}

/** La forme d'un id d'action du socle (`appScreenActionSchema`). */
const ID_D_ACTION = /^[a-z][a-z0-9-]*$/;

/**
 * UN ID D'ACTION LIBRE DANS UNE PORTE, tire d'un libelle : celui du libelle en
 * kebab-case, rendu unique parmi les ids deja poses (celui de « + Ajouter »
 * compris). Un libelle sans lettre donne `action`.
 *
 * @param {any} porte @param {string} label @param {string} [sauf] l'uid de l'action qui se nomme
 * @returns {string}
 */
export function idDActionLibre(porte, label, sauf = '') {
  const pris = new Set(['ajouter']);
  for (const a of Array.isArray(porte && porte.actions) ? porte.actions : []) {
    if (a && a.uid !== sauf && ID_D_ACTION.test(String(a.id || ''))) pris.add(a.id);
  }
  let base = kebab(label);
  if (!/^[a-z]/.test(base)) base = base === '' ? 'action' : 'action-' + base;
  let id = base;
  for (let n = 2; pris.has(id); n += 1) id = base + '-' + n;
  return id;
}

/**
 * CE QU'UNE ACTION FIGE UNE FOIS SON LIBELLE ECRIT : son `id` et, pour un
 * script, le nom de son effet. Le Studio le pose quand le libelle est valide
 * (au `blur`) ; la derivation en refait autant tant que personne ne l'a pose,
 * mais un renommage ne les change plus jamais : un flux s'abonne a l'id, et le
 * code de l'app porte le nom de l'effet.
 *
 * @param {any} porte @param {any} action
 * @returns {{ id?: string, fn?: string }} le patch a poser (vide : rien a figer)
 */
export function figerAction(porte, action) {
  const label = String((action && action.label) || '').trim();
  if (label === '') return {};
  const patch = {};
  if (!ID_D_ACTION.test(String(action.id || ''))) patch.id = idDActionLibre(porte, label, action.uid);
  if (
    declenchementDAction(action) === 'script' &&
    String(action.fn || '').trim() === '' &&
    String(action.fenetre || '').trim() === ''
  ) {
    const nom = clefDeChamp(label);
    if (nom !== '') patch.fn = nom;
  }
  return patch;
}

/** @returns {Object} un compteur d'ecran, vide. */
export function nouvelleStat() {
  /* `id` : vide a la naissance (la derivation le tire du libelle) ; la reprise
     d'une app y pose l'id que son fichier portait, pour ne pas le renommer. */
  return { uid: uid(), id: '', champ: '', calcul: '', valeur: '', label: '', labelMode: '', libre: '' };
}

/** @returns {Object} une vue 100% sur mesure (View Canvas), vide. */
/**
 * LES TROIS LISTES DE LA RACINE : liens, extra, notifications.
 *
 * Elles etaient creees par un litteral dans chaque section (`brief-ui/*.tsx`,
 * le bouton « + »), donc la relecture ne pouvait pas y AJOUTER une ligne : pas
 * de fabrique, pas de liste ouverte. Le fondateur (03/09) : « suite a la
 * relecture il n'y ait PLUS de truc agent decide de coche, il remplit TOUT ».
 * Une section qu'on peut confier et que l'agent ne peut pas remplir est une
 * case decorative. Meme fabrique des deux cotes, comme pour les autres.
 */

/**
 * UNE COLONNE DE TABLEAU (`vb["core.tableau"].colonnes[]`). Elle etait un litteral
 * du formulaire (`SectionDetailParVue.tsx`, le bouton « + colonne ») : la
 * relecture ne pouvait donc pas en ajouter, et un tableau confie restait
 * « aucune colonne » apres une passe complete (trouve sur l'app « Pret de
 * materiel », 03/09). Meme fabrique des deux cotes.
 * @returns {Object}
 */
export function nouvelleColonne() {
  return { uid: uid(), champ: '', triable: true, agg: '', editable: false };
}

/**
 * LES TROIS SORTES DE BLOC DU TABLEAU DE BORD (`vb["core.bord"].blocs`,
 * `docs/socle/tableau-de-bord.md`), liste FERMEE comme celle du schema du
 * socle (`appScreenBordBlocSchema`), avec le mot du formulaire. Les bornes sont
 * celles du schema : huit blocs, six chiffres par rangee, trois niveaux de tri.
 */
export const SORTES_DE_BLOC = Object.freeze([
  Object.freeze({ id: 'chiffres', mot: 'Chiffres' }),
  Object.freeze({ id: 'etapes', mot: 'Étapes' }),
  Object.freeze({ id: 'liste', mot: 'À traiter d’abord' }),
]);
export const BLOCS_MAX = 8;
export const CHIFFRES_MAX = 6;
export const TRI_DE_LISTE_MAX = 3;
export const PLAFOND_DE_LISTE_MAX = 50;

/**
 * UN CHIFFRE CLE d'un bloc `chiffres` : `compte` des fiches, ou `somme` d'un
 * champ nombre (`champ`, l'uid du champ). Sa CONDITION est une liste de
 * `clauses` toutes vraies a la fois (`nouvelleClause`), ou une condition brute `si`
 * que la reprise n'a pas su ramener a des clauses. Aucune : toutes les fiches.
 * `couleur` est facultative.
 * @returns {Object}
 */
export function nouveauChiffre() {
  return { uid: uid(), libelle: '', calcul: 'compte' };
}

/**
 * UNE CLAUSE de la condition d'un chiffre ou d'une liste : un champ (son uid), un
 * operateur (`OPERATEURS_DE_CLAUSE`) et, pour « est » et « n'est pas », les
 * valeurs STOCKEES retenues. Vide, elle ne s'ecrit pas.
 * @returns {Object}
 */
export function nouvelleClause() {
  return { uid: uid(), champ: '', op: 'parmi', valeurs: [] };
}

/**
 * UN BLOC DU TABLEAU DE BORD, vide, de la sorte demandee : `chiffres` porte son
 * premier chiffre, `etapes` ses valeurs repliees (valeurs STOCKEES du champ des
 * etapes), `liste` sa condition (`clauses`, comme un chiffre), son
 * tri (la pile de niveaux d'une porte) et son plafond.
 * @param {string} type @returns {Object}
 */
export function nouveauBloc(type) {
  if (type === 'chiffres') return { uid: uid(), type, libelle: '', chiffres: [nouveauChiffre()] };
  if (type === 'etapes') return { uid: uid(), type, libelle: '', repliees: [] };
  return { uid: uid(), type: 'liste', libelle: '', tri: [] };
}

/** @returns {Object} un lien vers une autre app : l'app, son jeu, ce qu'on en fait. */
export function nouveauLien() {
  return { uid: uid(), app: '', jeu: '', quoi: '' };
}

/**
 * UN LIEN ENTRE DEUX PORTES DU MEME ECRAN : « les interventions de CE
 * materiel ». A ne pas confondre avec `nouveauLien()` juste au-dessus, qui est
 * un lien vers une AUTRE APP : deux notions, un mot, et c'est le vocabulaire du
 * socle qui tranche (`appScreenLinkSchema`, packages/data-model/src/app-screens.ts).
 *
 * TROIS NOMS, ET AUCUNE REQUETE. `id` est ce que le fichier ecrit et ce que les
 * cartes citent ; `porte` est l'UID de la porte liee, jamais son id (le
 * formulaire refait l'id quand on renomme une porte, un lien tomberait en
 * silence) ; `par` est le champ du jeu LIE qui porte l'id de la fiche parente,
 * en nom NU, tel que le fichier l'ecrit.
 *
 * LE SENS DIRECT : `liste`, a la place de `par`, est le champ LISTE (`lien` +
 * `plusieurs`) de la fiche OUVERTE, nom NU lui aussi. EXACTEMENT UNE DES DEUX
 * CLEFS parle. La clef `liste` N'EXISTE PAS tant que le lien est en sens
 * « par » ; sa PRESENCE (meme vide) est le choix du sens, ce qui laisse a la
 * case sa place dans le formulaire avant d'etre remplie.
 *
 * @returns {Object}
 */
export function nouveauLienDePorte() {
  return { uid: uid(), id: '', porte: '', par: '' };
}

/**
 * UN CHAMP DE TOTAL d'une carte de fiche : « Interventions : 12 ».
 *
 * IL VIT DANS LA CARTE, a cote de ses champs, et son `uid` figure dans `keys`
 * A SON RANG : c'est ce qui garde l'ORDRE de la carte, que le socle rend tel
 * quel. `liee` est l'UID d'un lien de la porte ; `champ` est un nom de champ du
 * jeu LIE, ecrit tel quel (aucun uid du brief ne le designe : il appartient a
 * un autre jeu).
 *
 * @returns {Object}
 */
export function nouveauTotalDeCarte() {
  return { uid: uid(), libelle: '', liee: '', total: 'compte', champ: '' };
}

/**
 * LA PONDERATION D'UN CHAMP DE TOTAL, lue sur le brief : `total.fois` est
 * `{ par, champ }`, et son absence (ou deux cases vides) veut dire « pas de
 * ponderation ». Elle multiplie la valeur de chaque ligne par un nombre lu sur
 * la fiche que la ligne DESIGNE : `par` est le champ de reference du jeu LIE,
 * `champ` le champ numerique de la fiche designee. Les deux s'ecrivent tels
 * quels (ils appartiennent a d'autres jeux, aucun uid du brief ne les nomme).
 *
 * TROIS ETATS, ET UN SEUL EST ECRIT AVEC SA PONDERATION :
 *  - `aucune` : rien a ecrire, le total est une somme ordinaire ;
 *  - `complete` : `par` et `champ` sont remplis ET le calcul est « somme » (le
 *    schema ne pondere rien d'autre) ;
 *  - `incomplete` : une case seule, ou une ponderation sur un autre calcul. Le
 *    total n'est PAS ecrit : l'ecrire sans sa ponderation afficherait une
 *    somme de quantites sous le mot « Prix », un nombre plausible et faux.
 *
 * @param {Object} total
 * @returns {{ etat: 'aucune' } | { etat: 'complete', fois: { par: string, champ: string } } | { etat: 'incomplete' }}
 */
export function etatDePonderation(total) {
  const fois = total && typeof total === 'object' ? total.fois : undefined;
  const par = String((fois && fois.par) || '').trim();
  const champ = String((fois && fois.champ) || '').trim();
  if (par === '' && champ === '') return { etat: 'aucune' };
  if (par === '' || champ === '' || String((total && total.total) || '').trim() !== 'somme') {
    return { etat: 'incomplete' };
  }
  return { etat: 'complete', fois: { par, champ } };
}

/* ========================================================================== */
/* les fichiers joints : une capacite du jeu, une carte de la fiche           */
/* ========================================================================== */

/**
 * LES FAMILLES DE TYPES QU'UN JEU PEUT ACCEPTER, ET LEURS MOTS.
 *
 * RECOPIEES DE `@taki/data-model` (`fichiers-jeu.ts`), et c'est la meme raison
 * que pour la table des familles (`studio/familles.mjs`) : ce module tourne
 * dans le navigateur du Studio et sous `node --test`, sans le data-model. Un
 * test les recoupe avec l'original, dans les deux sens
 * (`packages/shell/tests/fichiers-joints-studio.test.mjs`) : une categorie
 * ajoutee d'un seul cote le casse.
 */
/** @type {readonly string[]} */
export const CATEGORIES_DE_FICHIERS = Object.freeze(['images', 'pdf', 'documents', 'videos', 'audio']);

/**
 * Le mot d'une categorie, tel que le Studio et l'ecran l'ecrivent.
 * @type {Readonly<Record<string, string>>}
 */
export const LIBELLES_CATEGORIES_DE_FICHIERS = Object.freeze({
  images: 'Images',
  pdf: 'PDF',
  documents: 'Documents',
  videos: 'Vidéos',
  audio: 'Audio',
});

/**
 * Les tailles de vignettes d'une carte de fichiers (`TAILLES_DE_VIGNETTES` du
 * data-model), recopiees pour la meme raison, et recoupees par le meme test.
 * La carte s'ouvre toujours en vignettes ; absente, la taille est les moyennes.
 * @type {readonly string[]}
 */
export const TAILLES_DE_VIGNETTES = Object.freeze(['petites', 'moyennes', 'grandes']);

/** Le plafond du socle, par fichier, en Mo : celui des regles Storage. */
export const TAILLE_MAX_FICHIER_MO = 25;

/** Le libelle d'une section de fichiers joints qu'on vient de poser. */
export const TITRE_CARTE_FICHIERS = 'Pièces jointes';

/**
 * LA SECTION DES FICHIERS JOINTS d'une fiche : `{ uid, title, fichiers: true }`,
 * et `modifiable: false` quand elle est en lecture seule (nue, elle suit les
 * droits de la porte). `vignettes` (`petites`, `moyennes` ou `grandes`) en regle
 * la taille des vignettes ou elle s'ouvre ; absente, les moyennes, le defaut qui ne s'ecrit pas. Ni champs, ni disposition : elle prend toujours la
 * rangee, comme une carte liee. Son `id`, quand le fichier en porte un, voyage
 * tel quel.
 *
 * @returns {Object}
 */
export function nouvelleCarteFichiers() {
  return { uid: uid(), title: TITRE_CARTE_FICHIERS, fichiers: true };
}

/** La carte est-elle la section des FICHIERS joints ? @param {unknown} carte */
export function estCarteFichiers(carte) {
  return carte !== null && typeof carte === 'object' && /** @type {any} */ (carte).fichiers === true;
}

/**
 * LA CAPACITE DE FICHIERS D'UN JEU, ou `null` quand elle est coupee.
 *
 * C'est LA lecture de `jeu.fichiers` : un objet l'ouvre, tout le reste (absent,
 * `null`, une valeur bricolee a la main) la coupe. Les sous-clefs ne sont pas
 * jugees ici, la derivation et le formulaire les lisent avec leurs defauts.
 *
 * @param {any} jeu @returns {{ types?: string[], tailleMaxMo?: number } | null}
 */
export function fichiersDuJeu(jeu) {
  const f = jeu && jeu.fichiers;
  return f !== null && typeof f === 'object' && !Array.isArray(f) ? f : null;
}

/** « Images, PDF » ou « Tous les types » : ce que le Studio dit d'une capacite ouverte. */
export function libelleDesTypesDeFichiers(fichiers) {
  const types = fichiers && Array.isArray(fichiers.types) ? fichiers.types : null;
  if (types === null || types.length === 0) return 'Tous les types';
  return types.map((t) => LIBELLES_CATEGORIES_DE_FICHIERS[t] || String(t)).join(', ');
}

/** @returns {Object} une automatisation : son type, la porte, les champs, ce qu'elle fait. */
export function nouvelExtra() {
  return { uid: uid(), type: 'auto', porte: '', champs: [], quoi: '' };
}

/** @returns {Object} une notification : la porte, les champs qui la declenchent, ce qu'elle dit. */
export function nouvelleNotification() {
  return { uid: uid(), porte: '', champs: [], quoi: '' };
}

/**
 * UNE VUE CANVAS DE LA PORTE (View Canvas, regle G23) : son nom, la prose de
 * l'agent, et son NIVEAU. `collection` (le defaut, celui d'avant la clef) monte
 * la vue dans le selecteur des vues de la porte ; `record` en fait une VUE DE
 * FICHE du jeu de la porte, a cote de « Fiche » (chantier `fiche-suivi`,
 * `docs/socle/fiche-suivi.md` section 3). Une vue d'un brief ecrit avant la
 * clef n'a pas `niveau` : elle se lit `collection`, ce qu'elle etait.
 */
export function nouvelleVueCustom(niveau = 'collection') {
  return { uid: uid(), nom: '', prompt: '', niveau: niveau === 'record' ? 'record' : 'collection' };
}

/** Le niveau d'une vue Canvas du brief : `record` seulement s'il est ecrit. */
export function niveauDeVueCustom(vue) {
  return vue && vue.niveau === 'record' ? 'record' : 'collection';
}

/**
 * UN CHEMIN PAR NIVEAU DE SUR-MESURE (retour du fondateur du 27/10) : la case
 * « View Canvas » (section « Vues ») ne dit QUE le niveau 3, des vues de
 * PLUSIEURS fiches ; une FICHE sur mesure (niveau 4, une vue de niveau
 * `record`) a son entree dans « Vues de fiche » (« + Fiche sur mesure »).
 *
 * LE BRIEF NE CHANGE PAS DE FORME : les deux vivent dans `porte.vuesCustom`,
 * et `customVues` reste l'interrupteur de TOUTE la liste (derivation,
 * nettoyage, synchronisation le lisent tel quel) : il est vrai des qu'une vue
 * Canvas, de l'un ou l'autre niveau, est posee. La case, elle, se LIT : cochee
 * quand la porte a une vue de collection (ou une liste encore vide, l'etat
 * d'un brief ecrit avant ce partage).
 *
 * @param {any} porte @returns {boolean}
 */
export function viewCanvasCoche(porte) {
  if (!porte || porte.customVues !== true) return false;
  const vues = Array.isArray(porte.vuesCustom) ? porte.vuesCustom : [];
  return vues.length === 0 || vues.some((v) => niveauDeVueCustom(v) === 'collection');
}

/**
 * LA PORTE APRES UN GESTE SUR SES VUES CANVAS D'UN NIVEAU : sa liste `suite`
 * pour ce niveau remplace l'ancienne, l'autre niveau reste tel quel, et
 * `customVues` suit (vrai tant qu'une vue Canvas reste, de l'un ou l'autre
 * niveau, ou que la case du niveau 3 reste cochee).
 *
 * @param {any} porte @param {'collection'|'record'} niveau @param {any[]} suite
 * @param {boolean} [cocheCollection] la case « View Canvas » apres le geste
 * @returns {{ customVues: boolean, vuesCustom: any[] }}
 */
export function vuesCustomDuNiveau(porte, niveau, suite, cocheCollection = viewCanvasCoche(porte)) {
  const vues = Array.isArray(porte?.vuesCustom) ? porte.vuesCustom : [];
  const autres = vues.filter((v) => niveauDeVueCustom(v) !== niveau);
  const vuesCustom = niveau === 'record' ? [...autres, ...suite] : [...suite, ...autres];
  return { customVues: vuesCustom.length > 0 || cocheCollection, vuesCustom };
}

/**
 * @typedef {Object} Jeu
 * @property {string} uid l'IDENTITE du jeu, et c'est tout le point de la version 2 :
 *   plusieurs portes le designent par cet uid (`porte.dsRef`), donc supprimer l'une
 *   d'elles ne peut plus emporter le jeu des autres
 * @property {string} nom le nom parlant, celui que la fiche du jeu porte dans
 *   `taki.screens.json` (`portes[].jeu.nom`)
 * @property {string} famille la famille du socle
 * @property {string} type le type local
 * @property {string} scope `org-wide` ou `owner-scoped`
 * @property {boolean} decoupe le jeu se tranche (filtres, favoris, etoile)
 * @property {string} dsDesc la description des donnees, en prose
 * @property {string} manque CE QUE L'APP EXIGERAIT ET QUE LA FAMILLE N'A PAS, en prose.
 *   C'est le seul endroit du formulaire ou un BLOCAGE SOCLE se declare AVANT le code :
 *   `prompt.mjs` le transmet a l'agent avec la consigne de le signaler et d'attendre,
 *   jamais de bricoler le chassis (regle 5 d'`AGENTS.md`). Vide vaut « rien a signaler »
 * @property {Champ[]} champs les champs declares
 * @property {Object} auto ce que le fondateur laisse a l'agent : `famille`, `type`,
 *   `scope`, `decoupe`. Il se lit avec un `!!` : une clef ABSENTE vaut `false`
 * @property {Object} instructions le texte libre laisse a l'agent, meme sac et meme
 *   lecture que celui d'une porte, un cran a cote
 * @property {{ types?: string[], tailleMaxMo?: number }} [fichiers] LES FICHIERS JOINTS,
 *   une capacite des DONNEES, ecrite telle que `taki.fields.json` la porte
 *   (`jeux[].fichiers`). ABSENTE, ELLE EST COUPEE, et c'est le defaut d'un jeu neuf :
 *   `nouveauJeu` ne la pose pas. `types` absent vaut tous les types, `tailleMaxMo`
 *   absent vaut le plafond du socle (`TAILLE_MAX_FICHIER_MO`)
 */

/**
 * Un jeu de donnees tout neuf, avec un champ vide dedans.
 *
 * SANS `fichiers`, ET C'EST VOULU : la capacite est coupee par defaut, et une
 * clef absente le dit deja. La poser ici a `null` ferait aussi naitre la clef
 * dans tout brief complete (`briefComplet`), donc dans chaque jeu repris d'une
 * app qui ne l'a jamais demandee.
 *
 * `auto` vaut TOUT A VRAI sur un jeu neuf (l'agent decide, tant que le fondateur
 * n'a rien tranche), alors qu'une clef absente se lit `false` : c'est exactement
 * pourquoi `migrer()` ne reconstruit jamais ce sac, il le porte tel quel.
 *
 * `manque` EST ICI PARCE QUE L'ECRAN LE DEMANDE. « Manque a la famille »
 * (`brief-ui/jeux.tsx`) ecrit cette clef depuis toujours, mais le modele ne la
 * portait pas et la migration la laissait tomber : le fondateur remplissait un
 * champ dans le vide. Elle est desormais du modele, elle survit a la migration
 * et `prompt.mjs` la transmet. Vide vaut « rien a signaler ».
 *
 * @returns {Jeu}
 */
export function nouveauJeu() {
  return {
    uid: uid(),
    nom: '',
    famille: '',
    type: '',
    scope: 'org-wide',
    decoupe: true,
    dsDesc: '',
    manque: '',
    /* Choix explicite: ce jeu ne demande ni fichier ni semis de demonstration. */
    sansDemo: false,
    champs: [nouveauChamp()],
    auto: { famille: true, type: true, scope: true, decoupe: true },
    instructions: {},
  };
}

/**
 * UN NOM PAR DEFAUT POUR UN JEU QUI N'EN HERITE AUCUN.
 *
 * Un jeu cree depuis une porte reprend le nom de la porte. Quand la porte n'en
 * a pas encore (« Porte sans nom »), le jeu naissait ANONYME, et le selecteur
 * de chaque porte affichait « jeu sans nom » autant de fois qu'il y en avait :
 * plus moyen de dire lequel est lequel. Constate par le fondateur sur deux jeux
 * crees a la suite.
 *
 * ON NE REUTILISE PAS UN NUMERO LIBERE : le nom sert a retrouver, pas a
 * compter. Deux jeux nommes « Jeu 2 » seraient pires que le trou.
 *
 * @param {Record<string, any>} brief
 * @returns {string}
 */
export function nomDeJeuLibre(brief) {
  const pris = new Set(
    (Array.isArray(brief && brief.jeux) ? brief.jeux : []).map((j) =>
      String((j && j.nom) || '').trim(),
    ),
  );
  let n = 1;
  while (pris.has('Jeu ' + n)) n += 1;
  return 'Jeu ' + n;
}

/**
 * UN JEU NEUF POUR CETTE PORTE, ET LA PORTE QUI LE DESIGNE : les deux moities du
 * meme fait, de quoi faire UNE seule ecriture.
 *
 * POURQUOI CETTE FONCTION EXISTE. Sortir les jeux des portes etait juste, mais
 * la contrepartie avait ete promise mot pour mot : « on peut toujours creer un
 * jeu depuis une porte, il atterrit dans la liste partagee au lieu d'etre
 * enfoui ». Sans elle, le cas COURANT (une porte, son jeu) obligeait a quitter
 * la section « Portes », creer dans « Jeux de donnees », revenir, designer.
 *
 * POURQUOI ELLE EST ICI, ET PAS DANS L'ECRAN. Deux ecritures successives
 * partiraient du meme brief fige par le rendu, et la seconde effacerait la
 * premiere : le jeu naitrait sans que personne ne le montre, ou la porte
 * designerait un uid qui n'existe pas. Une seule fonction rend les deux listes
 * ensemble, et elle est PURE, donc elle se prouve hors de React.
 *
 * LE JEU PREND LE NOM DE LA PORTE, quand elle en a un. Ce n'est pas une donnee
 * inventee : `verrousDuJeu` reproche immediatement « Un jeu de donnees n'a pas
 * de nom », et un raccourci qui fabrique son propre reproche n'est pas un
 * raccourci. Le nom reste un champ libre, editable dans « Jeux de donnees ».
 *
 * RIEN N'EST MUTE : le brief entre en lecture, les deux listes sortent neuves.
 *
 * @param {any} brief le brief courant
 * @param {any} porte la porte qui montrera ce jeu
 * @param {boolean} [manuel] une instance livrée ne délègue pas ce jeu neuf
 * @returns {{ jeux: any[], portes: any[] }}
 */
export function jeuNeufPourPorte(brief, porte, manuel = false) {
  const jeu = {
    ...nouveauJeu(),
    nom: String((porte && porte.label) || '').trim() || nomDeJeuLibre(brief),
  };
  if (manuel) jeu.auto = { famille: false, type: false, scope: false, decoupe: false };
  const jeux = Array.isArray(brief && brief.jeux) ? brief.jeux : [];
  const portes = Array.isArray(brief && brief.portes) ? brief.portes : [];
  return {
    jeux: [...jeux, jeu],
    portes: portes.map((p) => (p && p.uid === (porte && porte.uid) ? { ...p, dsRef: jeu.uid, sansJeu: false } : p)),
  };
}

/* ========================================================================== */
/* le jeu d'une porte : obligatoire en porte classique, facultatif en Canvas   */
/* ========================================================================== */

/*
 * LA REGLE DU FONDATEUR (10/10/2026). Une porte CLASSIQUE montre toujours un
 * jeu : sans lui elle est « à régler », et rien ne peut le lever. Une porte
 * CANVAS peut s'en passer, et le dit par l'option « Aucun jeu (voulu) » du
 * selecteur, qui pose `sansJeu: true`. Le meme selecteur vit a deux endroits
 * (la ligne de la porte dans la liste, et « 1. Porte ») : ces trois fonctions
 * sont sa seule logique, pour que les deux endroits ne divergent jamais.
 *
 * `sansJeu` RESTE DANS LE MODELE pour les briefs deja ecrits (et la reprise
 * d'une app livree), mais il n'a de sens que sur une porte Canvas : lu sur une
 * porte classique, il est ignore.
 */

/** La valeur du selecteur qui dit « aucun jeu, et c'est voulu » (porte Canvas). */
export const AUCUN_JEU_VOULU = '@aucun-jeu';

/**
 * La porte ne montre aucun jeu, et c'est une decision : seulement en Canvas.
 * @param {any} porte @returns {boolean}
 */
export function porteSansJeuVoulu(porte) {
  return Boolean(porte) && porte.custom === true && porte.sansJeu === true && !porte.dsRef;
}

/**
 * Ce que montre le selecteur de jeu d'une porte. Une porte Canvas sans jeu lit
 * « Aucun jeu (voulu) » meme sans la clef : pour elle, l'absence n'est pas un
 * trou. Une porte classique sans jeu lit la valeur vide, « à choisir ».
 * @param {any} porte @returns {string}
 */
export function valeurDuChoixDeJeu(porte) {
  if (porte && porte.dsRef) return String(porte.dsRef);
  return porte && porte.custom === true ? AUCUN_JEU_VOULU : '';
}

/**
 * Le patch de porte que pose un choix du selecteur. Choisir un jeu remet
 * `sansJeu` a faux ; « Aucun jeu (voulu) » n'existe qu'en Canvas, et une porte
 * classique qui le recevrait quand meme reste « à régler ».
 * @param {any} porte @param {string} valeur @returns {{ dsRef: string, sansJeu: boolean }}
 */
export function choixDeJeuDePorte(porte, valeur) {
  if (valeur === AUCUN_JEU_VOULU)
    return { dsRef: '', sansJeu: Boolean(porte) && porte.custom === true };
  return { dsRef: String(valeur || ''), sansJeu: false };
}

/**
 * LE CADRE D'UNE PORTE CANVAS NEUVE : la partie 1 (rail, identite, portes),
 * imposee, les smart boutons de la porte (`boutons`, que le gabarit rend et
 * qu'aucune surface n'a a alimenter : une porte neuve n'a pas a les perdre) et
 * le fond. Le titre est toujours la. Recherche et statistiques ne sont plus
 * gardees d'office : elles s'ajoutent dans le panneau Cadre. Une porte
 * historique (`keeps`, `canvas: {}`) garde sa lecture et son rendu.
 */
export const ELEMENTS_PORTE_CANVAS_NEUVE = Object.freeze(['rail', 'identite', 'portes', 'boutons', 'fond']);

/**
 * LE PASSAGE CLASSIQUE <-> CANVAS. Le jeu choisi est garde dans les deux sens.
 * Une porte Canvas « sans jeu (voulu) » qui redevient classique perd ce choix :
 * elle redevient « à régler » jusqu'au choix d'un jeu. Une porte qui devient
 * Canvas sans cadre deja ecrit recoit celui d'une porte neuve.
 * @param {any} porte @param {boolean} canvas @returns {any} la porte neuve
 */
export function basculerPorteCanvas(porte, canvas) {
  if (!canvas) return { ...porte, custom: false, sansJeu: false };
  const cadreEcrit = Array.isArray(porte?.canvasElements) || (Array.isArray(porte?.canvasKeeps) && porte.canvasKeeps.length > 0);
  return cadreEcrit ? { ...porte, custom: true } : { ...porte, custom: true, canvasElements: [...ELEMENTS_PORTE_CANVAS_NEUVE] };
}

/**
 * @typedef {Object} Porte
 * @property {string} uid
 * @property {boolean} custom la porte est decrite en prose, pas au formulaire : c'est le
 *   niveau SPACE CANVAS (`NIVEAUX_CANVAS.space`), et le nom de la clef ne bouge pas
 * @property {string} customPrompt cette prose
 * @property {string[]} [canvasAlimente] elements alimentes par la surface
 * @property {string[]} [canvasElements] liste canonique des elements du cadre
 * @property {string[]} canvasKeeps raccourci historique de lecture ;
 *   converti en porte.canvas.elements, jamais ecrit dans canvas.spaces
 * @property {string} id l'id technique de la porte
 * @property {string} label ce que lit l'utilisateur dans le menu
 * @property {string} compteur ce que compte la pastille du menu
 * @property {string} placeholder le texte gris de la barre de recherche
 * @property {string} videTitre ce que la zone 5 titre quand le jeu est vide
 * @property {string} videIndication l'indication sous ce titre (quoi faire)
 * @property {string} introuvableTitre ce que la zone 5 titre quand la fiche demandee n'existe pas
 * @property {string} introuvableIndication l'indication sous ce titre
 * @property {boolean} modeles les modeles de fiches sont actives sur la porte
 * @property {boolean} recurrences les plans recurrents sont actives sur la porte
 * @property {boolean} modifierDansLeTableau le tableau de la porte (`core.tableau`) s'edite en place. Projete en
 *   `portes[].modifierDansLeTableau: true`, et seulement si la porte monte le tableau
 * @property {boolean} fichePerso la porte offre « Fiche personnalisée » (`idDeFichePerso`), meme sans HTML encore.
 *   Projete en `portes[].fichePerso: true`, et seulement si la porte montre un jeu
 * @property {null | { lien: string, vue: string } | { vueDeFiche: string } | { derniereVue: true } | { porte: string, app: string }} auClic
 *   AU CLIC SUR UNE FICHE de la porte : `null` ouvre la fiche (le defaut du socle) ;
 *   `{ lien, vue }` entre dans la porte du lien `lien`, l'UID d'un lien de `liens` (comme
 *   `liee` d'une carte), filtree sur la fiche cliquee, dans la vue `vue` (vide : l'arrivee
 *   de la porte liee) ; `{ vueDeFiche }` ouvre la fiche dans cette vue de fiche du jeu (ou dans la
 *   fiche personnalisee de la porte, quand `fichePerso`) ; `{ derniereVue: true }` l'ouvre dans la derniere
 *   vue de fiche que la personne a utilisee sur la porte ;
 *   `{ porte, app }` ouvre la fiche dans une autre porte, l'UID d'une porte du brief
 *   (`app` vide) ou l'id d'une porte d'une autre app installee (`app` son id). Projete en
 *   `portes[].auClic` de `taki.screens.json` (`appScreenDoorSchema.auClic`)
 * @property {null | { champ: string, mode: 'flux' | 'selection', verrou?: object | object[] }[]} etapes LES EN-TETES
 *   DE LA RANGEE 3 D'UNE FICHE (« En tête de fiche »), dans l'ordre : chaque `champ` est
 *   l'UID d'un champ liste du jeu de la porte (un natif a catalogue compris), au plus une
 *   fois, `mode` ses etapes (`flux`) ou ses etiquettes (`selection`). `null` : rien dans la
 *   rangee. Un brief ecrit avant la liste porte un objet seul : il se lit par
 *   `enTetesDeLaPorte` (`@taki/data-model`), comme une liste d'un. Un en-tete peut porter des
 *   VERROUS (`etapes-verrou`, sous la clef `verrou` : une liste, ou un objet seul lu comme une
 *   liste d'un) : `si` est une condition du socle (les noms de champs de la
 *   declaration, comme `trancheCondition`), `avant` une valeur STOCKEE du champ (celles de rang
 *   inferieur se ferment) ou `depuis` (celle-ci et les suivantes), jamais les deux ; ni l'un
 *   ni l'autre : toute la piste. `raison` quelques mots. Projete en
 *   `portes[].etapes`
 * @property {null | string[]} vuesFiche LES VUES DE FICHE que la porte montre, hors
 *   « Fiche » (toujours la) : `null` montre les vues HTML du jeu et aucune vue
 *   Canvas de fiche (eteinte tant qu'on ne la coche pas, 27/10), une liste dit
 *   exactement celles qui sont montrees.
 *   Projete en `portes[].vuesFiche`
 * @property {boolean} navigation la porte s'affiche dans la NAVIGATION (menu de l'app,
 *   raccourcis du dock). `false` : on n'y entre que par un lien d'une autre porte. Projete
 *   en `portes[].navigation: false`, et seulement pour le refus (`appScreenDoorSchema.navigation`)
 * @property {string} dsRef l'uid du JEU que cette porte montre (`brief.jeux[].uid`), ou
 *   vide quand elle n'en montre aucun. AVANT LA VERSION 2 c'etait l'uid de la PORTE
 *   reprise : meme clef, autre referent, d'ou la montee de `VERSION_MODELE`
 * @property {boolean} sansJeu la porte ne montre AUCUN jeu, et c'est une decision. Sans
 *   cette clef, `dsRef` vide voulait dire deux choses (« pas encore choisi » et « rien a
 *   montrer »), donc une app qui porte une porte volontairement vide restait bloquee sur
 *   un verrou que rien ne pouvait lever
 * @property {boolean | undefined} [tranchable] le choix propre de cette porte ; absent,
 *   elle suit `jeu.decoupe`. Deux portes du meme jeu peuvent donc differer.
 * @property {string} dsDistinct legs de V1 : la tranche, avant qu'elle ait ses propres cles
 * @property {'tout'|'filtre'|'libre'} tranche quelle part du jeu cette porte montre. LA
 *   TRANCHE RESTE UN FAIT DE LA PORTE : trois portes sur le meme jeu en montrent trois
 *   parts differentes
 * @property {Object} [trancheCondition] la condition du socle qui garde les fiches de la
 *   tranche « filtre » (`tranche` de la porte dans `taki.screens.json`)
 * @property {string} trancheLibre
 * @property {string[]} vues les ids de VUES montees par la porte
 * @property {string} arrivee la vue affichee en arrivant
 * @property {boolean} customVues
 * @property {Object[]} vuesCustom
 * @property {Object} vb le detail par vue : une entree par id de vue. Une CARTE DE CHAMPS de
 *   `vb['core.fiche'].cards` est `{ uid, title, keys, edit, disposition, totaux? }`, et
 *   `disposition` vaut `demi`, `pleine` ou `titre-pleine` (absente = `demi`, lue par
 *   comparaison explicite). Une carte de champs porte aussi, FACULTATIVE, `placement: 'droite'`
 *   (son defaut, principal, ne se stocke pas : pas de montee de `VERSION_MODELE`). Elle a remplace le booleen `wide`, qui ne savait dire que deux
 *   des trois etats que le socle rendait deja. Une CARTE LIEE, elle, est
 *   `{ uid, title, liee, vue, plafond?, colonnes? }` : aucune disposition, elle prend toujours la
 *   rangee. `colonnes` (tableau seulement) est la liste ORDONNEE des colonnes du tableau de la
 *   carte, des UID de champs du jeu de la porte LIEE (ou `std:<champ>`), comme `colonnes` d'une vue.
 *   La CARTE DES FICHIERS JOINTS est `{ uid, title, fichiers: true, modifiable?: false, vignettes?: 'petites' | 'moyennes' | 'grandes', id? }`
 *   (`nouvelleCarteFichiers`) : une seule par fiche, toujours la rangee, et rien a l'ecran
 *   tant que le jeu de la porte n'ouvre pas les fichiers (`jeu.fichiers`). Une REGLE DE
 *   SURBRILLANCE de `vb['core.kanban'].surbrillances` est `{ uid, champ, valeur?, moi?,
 *   couleur, etiquette? }` : `champ` est l'uid d'un champ (ou `std:<champ>`), ou `nouveau`
 *   pour la fiche jamais ouverte ; `moi: true` sur une personne. Une condition que la
 *   reprise ne ramene pas a cette forme reste brute dans `si`, et `champ` est alors absent.
 *   Les BLOCS du tableau de bord, `vb['core.bord'].blocs`, sont `{ uid, type, libelle?, ... }` :
 *   `chiffres` porte `chiffres: [{ uid, libelle, calcul, champ?, clauses? | si?, couleur? }]`, `etapes`
 *   porte `repliees` (valeurs stockees du champ des etapes), `liste` porte `clauses?`, `tri`
 *   (une pile de niveaux, comme `tri` de la porte) et `plafond?`. `clauses` sont les conditions
 *   d'un chiffre ou d'une liste, toutes vraies a la fois : `{ uid, champ, op, valeurs? }`, `champ`
 *   etant l'uid d'un champ (ou `std:<champ>`) et `op` l'un de `OPERATEURS_DE_CLAUSE`
 *   (`derive.mjs`). Une condition que la reprise ne ramene pas a des clauses (un `ou`, un `et`
 *   imbrique) reste brute dans `si`, et `clauses` est alors absent
 * @property {Object} filtres LE CATALOGUE DE FILTRES DE LA PORTE, jamais une phrase :
 *   `{ champ, autres }`. `champ` est l'UID d'un champ declare de nature « liste de
 *   choix » : le socle en tire un filtre PAR VALEUR de son catalogue, et la comparaison
 *   (une egalite) lui appartient. `autres` porte ce qui n'est pas « ce champ vaut cette
 *   valeur » : `{ uid, label, garde }`, ou `garde` reste de la PROSE parce que c'est un
 *   predicat TypeScript que l'agent ecrira, et ou le nom d'appel se DERIVE du libelle
 * @property {Object[]} favoris `{ uid, label, filtres, tri }`. `filtres` porte des
 *   REFERENCES aux filtres coches, `valeur:<valeur stockee>` pour une valeur du catalogue
 *   `parChamp` et `filtre:<uid>` pour un filtre nomme (`REF_VALEUR`, `REF_FILTRE`) : un
 *   libelle y serait a la fois fragile (le renommer perd le favori) et ambigu (deux
 *   sources, un seul espace de noms). `tri` est une pile de niveaux, meme forme que
 *   `porte.tri`
 * @property {Object[]} tri LA PILE DE TRI PAR DEFAUT, ordonnee : un niveau par entree,
 *   `{ uid, champ, role, mot, sens }`. Un niveau porte un CHAMP (son uid) OU un ROLE
 *   (`ROLES_TRI`), jamais les deux ; `mot` est le libelle d'un role, que rien d'autre ne
 *   connait. C'etait une CHAINE a virgules (« Nom croissant, puis date ») : le sens y
 *   etait dans les mots, et personne ne le lisait
 * @property {Object[]} stats
 * @property {boolean} statsAuto les compteurs sont laisses a l'agent (section 8)
 * @property {Object} ajout LA CREATION DE CETTE PORTE : `{ defauts, open }`. Elle ne porte
 *   plus de liste `champs` (la fiche de creation montre les cartes de la fiche : il n'y a
 *   plus rien a cocher), et porte a la place ce qu'une fiche NEUVE a deja :
 *   `defauts: [{ uid, valeur }]`, projete en `creation.defauts` dans `taki.screens.json`.
 *   `ajout.champs` d'un brief deja pose sur le disque est simplement IGNORE
 * @property {Object[]} actions chacune porte son `variant` : '' (defaut du socle),
 *   'secondary' ou 'primary' (`VARIANTES`, `projectionPortes.ts`). SON NOM D'APPEL NE
 *   SE TAPE PAS : il se derive du libelle (`clefDeChamp`), comme la clef d'un champ se
 *   derive du sien. Seul l'EFFET reste de la prose, et il le restera : il ferme sur
 *   l'etat de l'ecran, il ne deviendra jamais de la donnee
 * @property {Object} verbes archiver, desarchiver, supprimer
 * @property {Object} ftAuto ce que l'agent decide, cote filtres et tri
 * @property {Object} instructions le texte libre laisse a l'agent, une entree par UNITE
 *   deleguee de la porte : la clef est l'identifiant de la SECTION (`detail`, `fiche`,
 *   `filtres`, `stats`) ou celui d'un POINT FIN (`vb["core.liste"].auto`,
 *   `ftAuto.tri`). La valeur est une chaine ; absente vaut vide vaut « rien a
 *   transmettre ». Le catalogue de ces identifiants vit dans `brief/derive.mjs`.
 */

/**
 * Une porte toute neuve, avec un champ vide dedans : c'est le `newPorte()` de V1.
 *
 * UN SEUL ecart avec `newPorte()`, et sans effet sur le prompt : `ftAuto` est
 * pose ici a `{}`, la ou V1 le creait a la volee (`ft(p)`) et ou il n'apparaissait
 * donc que dans les fichiers deja sauves. Absent ou vide, il se lit pareil : tout
 * a `false`, parce que ses trois points se lisent avec un `!!`
 * (`pointsDeSection` dans `brief/derive.mjs`, `filtreDelegue` dans
 * `brief/prompt.mjs`). C'est cette LECTURE qui rend l'ecart sans consequence, et
 * elle est verifiable ; le fichier de demo de V1 qui servait autrefois de preuve
 * ici n'existe plus dans ce depot, et un chemin mort ne prouve rien.
 *
 * LE JEU DE DONNEES N'EST PLUS DEDANS. Une porte neuve nait sans jeu (`dsRef`
 * vide) : les champs se declarent une fois, dans `brief.jeux[]`, et la porte
 * pointe celui qu'elle montre. Le sac `auto` qui portait « l'agent decide » a
 * suivi le jeu (`nouveauJeu`).
 *
 * @returns {Porte}
 */
export function nouvellePorte() {
  return {
    uid: uid(),
    custom: false,
    customPrompt: '',
    /* Absent elements : lire ce raccourci avec le rendu historique. */
    canvasKeeps: [],
    id: '',
    label: '',
    compteur: '',
    placeholder: '',
    /*
     * CE QUE LA PORTE DIT QUAND SON JEU EST VIDE, ET QUAND LA FICHE DEMANDEE
     * N'EXISTE PAS : un titre et, dessous, l'indication qui dit quoi faire
     * (« Aucun contact » / « Ajoutez votre premier contact »). Quatre chaines
     * A PLAT, comme `placeholder` : vides, rien ne s'ecrit et le socle parle en
     * termes generiques. Ces mots vivaient dans le code de chaque ecran ; une
     * app sans ecran n'a plus que sa declaration pour les porter.
     */
    videTitre: '',
    videIndication: '',
    introuvableTitre: '',
    introuvableIndication: '',
    /* FAUX, la valeur NEUTRE : une clef absente d'un brief ecrit avant elle se
       lit `=== true` donc « pas de modeles », exactement comme ici. Une clef
       facultative de plus ne monte donc pas `VERSION_MODELE` ; `briefComplet`
       la repose sur les portes d'un fichier qui ne l'a pas. */
    modeles: false,
    /* FAUX, pour la meme raison que `modeles` : l'absence se lit non. */
    recurrences: false,
    /* FAUX, pour la meme raison : l'absence se lit non (le tableau se lit, un clic ouvre la fiche). */
    modifierDansLeTableau: false,
    /* FAUX, pour la meme raison : l'absence se lit non (la porte n'offre pas de fiche personnalisee). */
    fichePerso: false,
    /*
     * L'UID DU JEU QUE CETTE PORTE MONTRE, vide sur une porte neuve. Le jeu vit
     * a la racine du brief (`brief.jeux[]`) : une porte le DESIGNE, elle ne le
     * contient plus. Vide se lit « aucun jeu choisi », ce qui est exactement ce
     * qu'une porte neuve dit.
     */
    dsRef: '',
    /*
     * UNE PORTE QUI NE MONTRE AUCUN JEU, ET QUI L'ASSUME.
     *
     * Sans cette clef, `dsRef` vide voulait dire deux choses : « je n'ai pas
     * encore choisi » (un trou du formulaire) et « je n'ai rien a montrer »
     * (une decision). Le studio ne pouvait donc pas reprendre une app qui porte
     * une porte volontairement vide : elle restait bloquee sur un verrou qu'on
     * ne pouvait pas lever. FAUX est la valeur neutre : une porte neuve n'a pas
     * choisi, elle n'a pas decide de se passer de jeu.
     */
    sansJeu: false,
    dsDistinct: '',
    tranche: 'tout',
    trancheLibre: '',
    vues: ['core.liste', 'core.fiche'],
    arrivee: 'core.liste',
    customVues: false,
    vuesCustom: [],
    vb: {},
    /*
     * LE CATALOGUE DE FILTRES, UN OBJET ET NON UNE LISTE, parce que le socle
     * n'accepte qu'UN `parChamp` par porte (`appScreenFiltersSchema`) : une
     * liste aurait laisse croire le contraire. Vide se lit « aucun filtre »,
     * donc toute la porte, ce qui est exactement ce qu'une porte neuve dit.
     */
    filtres: { champ: '', autres: [] },
    /*
     * LES LIENS DE CETTE PORTE vers d'autres portes du MEME ecran. Vide se lit
     * « aucun lien », ce qui est le comportement d'avant : une clef facultative
     * de plus ne monte donc pas `VERSION_MODELE`, et `briefComplet` la repose
     * sur les portes d'un brief qui ne l'a pas.
     */
    liens: [],
    /*
     * AU CLIC SUR UNE FICHE : `null` ouvre la fiche, le comportement de
     * toujours. Sinon `{ lien, vue }`, ou `lien` est l'UID d'un lien de cette
     * porte (jamais son id, qui se retape) et `vue` une vue de collection de
     * la porte liee, vide pour son arrivee. Clef facultative : `null` se lit
     * comme son absence, donc pas de montee de `VERSION_MODELE`, et
     * `briefComplet` la repose.
     */
    auClic: null,
    /*
     * « EN TÊTE DE FICHE » (chantier `fiche-suivi`) : `null`, rien dans la
     * rangee 3, le comportement de toujours. Sinon la liste `[{ champ, mode }]`,
     * ou chaque `champ` est l'UID d'un champ liste du jeu. Clef facultative :
     * `null` se lit comme son absence, `briefComplet` la repose.
     */
    etapes: null,
    /* LES VUES DE FICHE MONTREES : `null`, toutes celles du jeu (le defaut du
       socle). Une liste restreint ; « Fiche » n'y figure jamais, elle est
       toujours la. Meme contrat de clef facultative. */
    vuesFiche: null,
    /*
     * DANS LA NAVIGATION : VRAI, le comportement de toujours (absent du fichier
     * vaut oui). FAUX : la porte quitte le menu et les raccourcis, on n'y entre
     * que par un lien. Clef facultative : `briefComplet` la repose a vrai sur
     * un brief ecrit avant elle, donc pas de montee de `VERSION_MODELE`.
     */
    navigation: true,
    favoris: [],
    /* LA PILE D'ARRIVEE, ordonnee : le premier niveau commande, les suivants
       departagent. Vide, l'app arrive dans l'ordre du chargeur. */
    tri: [],
    stats: [],
    /* FAUX, la valeur NEUTRE : une clef absente d'un brief ecrit avant elle se
       lit `=== true` donc « pas delegue », exactement comme ici. */
    statsAuto: false,
    /* PAS DE LISTE `champs` : la creation est une FICHE VIDE en zone 5, elle
       montre les cartes de la fiche, donc il n'y a rien a cocher. Vide se lit
       « aucun defaut », ce qui est exactement ce qu'une porte neuve dit. */
    ajout: { defauts: [], refuse: false, open: false },
    actions: [],
    verbes: { archive: true, unarchive: true, remove: false },
    ftAuto: {},
    /*
     * UN SEUL SAC PAR NIVEAU, et non une clef a cote de chaque drapeau. Un sac
     * ENTIEREMENT absent est le seul cas que `completerSurface` sait completer
     * sans retourner un reglage : N clefs facultatives seraient N pieges de la
     * famille de `demoTousJeux`. Il est pose a `{}`, comme `ftAuto` et pour la
     * meme raison : absent et vide se lisent pareil, rien n'est delegue.
     */
    instructions: {},
  };
}

/**
 * @typedef {Object} Brief
 * @property {number} version la version de la FORME, pas celle de l'app
 * @property {string} nom
 * @property {string} menuLabel libellé court affiché dans le menu
 * @property {string} id
 * @property {string} icone
 * @property {string} description
 * @property {string} auteur
 * @property {string} agent
 * @property {boolean} appCustom l'app sort du gabarit ENTIEREMENT : c'est le niveau APP
 *   CANVAS (`NIVEAUX_CANVAS.app`), et le nom de la clef ne bouge pas
 * @property {string} appCustomPrompt
 * @property {string[]} [appCanvasAlimente] elements alimentes par la surface
 * @property {string[]} [appCanvasElements] liste canonique des elements du cadre
 * @property {string[]} appCanvasKeeps raccourci historique de lecture ;
 *   converti en canvas.app.elements
 * @property {string} canvasGrantedOn le jour ou le fondateur a accorde la sortie du gabarit,
 *   `AAAA-MM-JJ` ; devient `canvas.grantedOn`, que la fiche EXIGE
 * @property {string} pourQui
 * @property {string} probleme
 * @property {string} geste
 * @property {Jeu[]} jeux LES JEUX DE DONNEES DE L'APP, declares UNE fois et partages
 *   par les portes qui les montrent. Ils se declarent AVANT les portes, parce que c'est
 *   l'ordre des DEPENDANCES : une vue, un binding et une fiche ont tous besoin des champs
 * @property {Porte[]} portes
 * @property {Object[]} vuesHtml les vues HTML publiees de l'ecran, reprises telles quelles
 *   (`VueHtmlDeclaree` de `@taki/data-model`) : le Studio les liste et les retire, il ne
 *   les ecrit jamais
 * @property {Object[]} vuesFicheSansPorte les vues Canvas de FICHE (`ecran.vuesCustom` de
 *   niveau `record`) qu'aucune porte classique de leur jeu ne porte, reprises telles quelles
 * @property {string} demoVolume
 * @property {boolean} demoAuto      le jeu de demo est laisse a l'agent
 * @property {boolean} demoTousJeux  la demo couvre TOUS les jeux de donnees
 * @property {string[]} demoJeux     sinon, les uid des JEUX qu'elle couvre
 * @property {string} demoDesequilibre
 * @property {string} demoVocab
 * @property {Object[]} liens
 * @property {boolean} liensAuto     la section Liens est laissee a l'agent
 * @property {Object[]} extra
 * @property {boolean} extraAuto     la section Automatisations est laissee a l'agent
 * @property {Object[]} notifications une notification par entree : `{ uid, porte, champs, quoi }`
 * @property {boolean} notificationsAuto la section Notifications est laissee a l'agent
 * @property {Object} instructions le texte libre laisse a l'agent, une entree par SECTION
 *   deleguee de la vue d'ensemble (`liens`, `extra`, `notifications`, `demo`).
 *   Meme forme et meme lecture que le sac d'une porte, un cran plus haut.
 */

/**
 * Un brief tout neuf : le `blank()` de V1, plus `version`. Sans porte, comme V1 :
 * la premiere porte est ajoutee par le fondateur.
 * @returns {Brief}
 */
export function nouveauBrief() {
  return {
    version: VERSION_MODELE,
    nom: '',
    menuLabel: '',
    id: '',
    icone: '',
    description: '',
    auteur: '',
    agent: '',
    appCustom: false,
    appCustomPrompt: '',
    /* Absent elements : lire ce raccourci avec le rendu historique. */
    appCanvasKeeps: [],
    /*
     * LA DATE D'ACCORD DU FONDATEUR, et elle n'est pas decorative : la fiche
     * `taki.app.json` EXIGE `canvas.grantedOn` des qu'un niveau est declare
     * (`scripts/check-app-entities.mjs`). Sans elle, l'app nait avec une fiche
     * que `pnpm apps:check` refuse, et le fondateur decouvre le probleme apres
     * la creation. `validate.mjs` la reclame donc AVANT.
     *
     * Elle vit a la RACINE et pas sur la porte : la fiche n'en porte qu'UNE
     * pour toute l'app, quel que soit le nombre de niveaux declares. Vide par
     * defaut, donc absente et vide se lisent pareil.
     */
    canvasGrantedOn: '',
    pourQui: '',
    probleme: '',
    geste: '',
    /*
     * LES JEUX AVANT LES PORTES, dans le modele comme dans le rail : une porte
     * POINTE un jeu (`dsRef`), donc le jeu ne peut pas dependre d'elle. Vide par
     * defaut, et vide se lit comme absent : un brief d'avant la version 2 passe
     * par `migrer`, qui remonte le jeu de chaque porte ici.
     */
    jeux: [],
    portes: [],
    /*
     * LES VUES HTML DE L'ECRAN (`ecran.vuesHtml`, chantier `fiche-suivi`) :
     * deposees par le MCP (`vue_publier`) dans la declaration de l'org, jamais
     * ecrites ici. Le brief les REPREND telles quelles pour les lister et les
     * retirer ; la derivation les reecrit a l'identique. Vide se lit comme
     * absent : une app sans vue HTML n'ecrit pas la clef.
     */
    vuesHtml: [],
    /*
     * LES VUES DE FICHE SANS PORTE CLASSIQUE (`ecran.vuesCustom` de niveau
     * `record`, chantier `fiche-suivi`) : une vue de fiche se range dans une
     * porte classique de son jeu (`porte.vuesCustom`), et la derivation ne lit
     * aucune vue Canvas dans une porte Canvas. Quand le jeu n'a QUE des portes
     * Canvas, la reprise garde la vue ici, telle que le fichier la declare, et
     * la derivation la reecrit a l'identique : elle ne se perd pas. Vide se
     * lit comme absent.
     */
    vuesFicheSansPorte: [],
    /*
     * LES DRAPEAUX DE SECTION. Liens, Automatisations et Notifications sont les
     * sections deleguables qui n'ont AUCUN point fin : elles portent donc leur
     * propre drapeau, sur le modele exact de `demoAuto`.
     *
     * Pose a `false`, lu avec `=== true` : ABSENT vaut « pas delegue », donc un
     * brief ecrit avant ces clefs se comporte exactement comme avant et les
     * trois goldens ne bougent pas d'un caractere.
     *
     * Le piege est deja paye sur `demoTousJeux`, pose a `true` : la valeur du
     * constructeur ne dit RIEN de ce que vaut une clef absente, c'est la LECTURE
     * qui le decide. Un defaut a `false` est le seul qui coincide avec l'absence
     * sans qu'on ait a y penser. Un futur drapeau de section se pose donc a
     * `false`, ou il faut ecrire sa lecture pour l'absence, pas pour le defaut.
     *
     * PLUS DE LISTE `reglages` NI DE `reglagesAuto` (11/10/2026) : les
     * Parametres d'une app viennent du socle, une liste libre de reglages ne
     * menait a aucun code. Un ancien brief qui les porte les perd a la lecture
     * (`oublierLesReglages`, dans `migrer`).
     */
    demoVolume: '',
    /*
     * Le neuvieme point de delegation. Comme les huit autres il se lit avec un
     * `!!` : absent vaut « pas delegue », donc un brief d'avant cette cle se
     * comporte exactement comme avant et aucun golden ne bouge.
     */
    demoAuto: false,
    /*
     * Quels jeux la demo remplit. `demoTousJeux` est vrai par DEFAUT : c'est le
     * cas courant, et surtout c'est le seul qui n'ait rien a maintenir quand un
     * jeu s'ajoute. Decoche, `demoJeux` porte les uid des JEUX choisis (avant la
     * version 2 c'etaient des uid de PORTE : `migrer` les traduit).
     */
    demoTousJeux: true,
    demoJeux: [],
    demoDesequilibre: '',
    demoVocab: '',
    liens: [],
    liensAuto: false,
    extra: [],
    /** Voir `liensAuto` : meme modele, meme lecture, meme raison. */
    extraAuto: false,
    /*
     * LES NOTIFICATIONS SONT UNE SECTION A ELLES, plus un type d'automatisation :
     * « Notifictions est retiré de automatisations et devient une porte a part
     * entière du formulaire ». Une automatisation AGIT sur les donnees, une
     * notification PREVIENT quelqu'un ; le studio d'une app LUE les separait
     * deja, le formulaire d'une app neuve les melangeait.
     *
     * CE QU'UNE NOTIFICATION DECLARE N'EST PAS INVENTE ICI : c'est exactement ce
     * qu'une automatisation de type `notif` declarait, moins son type -- la
     * porte visee, les champs concernes, et ce qui doit arriver. Le canal, le
     * destinataire et le texte ne sont PAS declares : rien n'envoie de mail
     * aujourd'hui (`XTYPES.notif`, `brief/familles.mjs`), et une forme inventee
     * avant le chantier serait a defaire.
     *
     * CLEFS FACULTATIVES, DONC PAS DE MONTEE DE `VERSION_MODELE` : absentes,
     * elles se lisent « aucune notification, rien de delegue », ce qui est le
     * comportement d'un brief ecrit avant elles.
     */
    notifications: [],
    /** Voir `liensAuto` : meme modele, meme lecture, meme raison. */
    notificationsAuto: false,
    /** Voir `instructions` de `nouvellePorte()` : meme sac, meme lecture, un cran plus haut. */
    instructions: {},
  };
}

/* ========================================================================== */
/* migrer                                                                     */
/* ========================================================================== */

/**
 * LA LISTE LIBRE DE REGLAGES EST PARTIE, quelle que soit la version du brief :
 * `reglages`, `reglagesAuto` et la consigne `instructions.reglages` d'un ancien
 * brief s'effacent a la lecture, sans erreur et sans montee de version (une
 * clef absente se lit comme avant). Le prochain enregistrement ne les porte plus.
 * @param {Record<string, any>} b
 */
function oublierLesReglages(b) {
  delete b.reglages;
  delete b.reglagesAuto;
  if (b.instructions && typeof b.instructions === 'object') delete b.instructions.reglages;
}

/**
 * LES NATURES DOUBLONS DU 27/10/2026 SE FONDENT DANS L'EXISTANT, quelle que
 * soit la version du brief, comme la liste de reglages ci-dessus. Le Studio de
 * recette les a proposees quelques heures a cote des natures d'avant, jamais
 * livrees ; un brief qui en porte une la relit dans la nature qui l'a absorbee,
 * avec le reglage qui dit ce qu'elle ajoutait (`docs/socle/champs-types.md`,
 * section 1). Sans ce pas, sa nature serait inconnue et le champ ECARTE du
 * fichier. Idempotent : une nature d'avant ne bouge pas.
 * @param {Record<string, any>} b
 */
function fondreLesNaturesDoublons(b) {
  for (const jeu of Array.isArray(b.jeux) ? b.jeux : []) {
    for (const c of jeu && Array.isArray(jeu.champs) ? jeu.champs : []) {
      if (!c || typeof c !== 'object') continue;
      if (c.nature === 'montant') c.nature = 'prix';
      else if (c.nature === 'durée (heures et minutes)') c.nature = 'durée';
      else if (c.nature === 'étiquettes libres') c.nature = 'liste de textes';
      else if (c.nature === 'pourcentage (jauge)') Object.assign(c, { nature: 'pourcentage', affichage: 'jauge' });
      else if (c.nature === 'liens vers des fiches') Object.assign(c, { nature: 'champ lié', plusieurs: true });
    }
  }
}

/**
 * Copie profonde d'une valeur JSON (objets simples, tableaux, primitifs).
 * Un brief ne contient rien d'autre : il fait l'aller-retour par le disque.
 * @template T
 * @param {T} valeur
 * @returns {T}
 */
function cloner(valeur) {
  if (Array.isArray(valeur)) return /** @type {any} */ (valeur.map(cloner));
  if (valeur && typeof valeur === 'object') {
    /** @type {Record<string, unknown>} */
    const sortie = {};
    for (const cle of Object.keys(valeur)) {
      /*
       * `defineProperty` et NON `sortie[cle] = ...`. Un brief vient d'un fichier sur le
       * disque : `JSON.parse` fabrique une clef `__proto__` en propriete PROPRE, mais
       * l'affectation par crochets, elle, declenche le SETTER `__proto__` et fait du
       * contenu le prototype du clone. `completerSurface` verrait alors les valeurs
       * HERITEES comme presentes, ne poserait aucun defaut, et le brief migre perdrait
       * des clefs EN SILENCE (`genererPrompt` sortant « l'app MyTaki « undefined » »).
       * Meme raison, meme forme que le `cloner` de `revue.mjs`.
       */
      Object.defineProperty(sortie, cle, {
        value: cloner(/** @type {any} */ (valeur)[cle]),
        enumerable: true,
        writable: true,
        configurable: true,
      });
    }
    return /** @type {any} */ (sortie);
  }
  return valeur;
}

/**
 * Pose les cles ABSENTES de `defauts` sur `cible`, sans jamais toucher a une cle
 * deja presente, et SANS DESCENDRE dedans.
 *
 * Ne pas descendre n'est pas de la paresse, c'est la regle de surete : dans V1
 * plusieurs sacs (`ds.auto`, `verbes`, `vb`, `ftAuto`) se lisent avec un `!!`,
 * donc une sous-cle absente vaut `false`, alors que le constructeur la pose a
 * `true`. Les completer retournerait le reglage du fondateur. On ne complete donc
 * qu'un sac entierement absent, jamais un sac a moitie rempli.
 *
 * @template {Record<string, any>} T
 * @param {T} cible
 * @param {Record<string, any>} defauts
 * @returns {T}
 */
function completerSurface(cible, defauts) {
  for (const cle of Object.keys(defauts)) {
    if (cible[cle] === undefined) /** @type {any} */ (cible)[cle] = defauts[cle];
  }
  return cible;
}

/**
 * Les cinq rustines que V1 appliquait au chargement (`load()`), portees telles
 * quelles. Elles operent sur une porte DEJA clonee, avant qu'on la complete.
 * @param {Record<string, any>} p
 * @returns {void}
 */
function reparerPorteV1(p) {
  /* 1. la nature « montant » a ete renommee « prix », et a gagne un symbole.
     ELLE TOURNE AVANT `sortirLesJeux`, donc sur le sac `ds` encore en place :
     les champs corriges partent ensuite tels quels dans le jeu extrait. */
  const champs = p.ds && Array.isArray(p.ds.champs) ? p.ds.champs : [];
  for (const c of champs) {
    if (c && c.nature === 'montant') {
      c.nature = 'prix';
      c.symbole = c.symbole || '€';
    }
  }

  /* 2. sur la fiche, la position d'une carte est devenue un booleen `wide`.
     ELLE POSE ENCORE `wide`, ET C'EST VOULU : `nommerLaDisposition` (forme 8)
     tourne APRES et le traduit en `disposition`. Deux rustines qui se suivent,
     chacune ne connaissant que son propre pas : la chaine tient. */
  const fiche = (p.vb || {})['core.fiche'];
  if (fiche && Array.isArray(fiche.cards)) {
    for (const c of fiche.cards) {
      if (!c || typeof c !== 'object') continue;
      if (c.pos === 'pleine largeur') c.wide = true;
      delete c.pos;
    }
  }

  // 3. `dsDistinct` decrivait la tranche avant qu'elle ait ses propres cles.
  if (p.dsDistinct && !p.tranche) {
    p.tranche = 'libre';
    p.trancheLibre = p.dsDistinct;
  }

  // 4. une porte sans tranche montre tout le jeu.
  if (!p.tranche) p.tranche = 'tout';

  // 5. des vues sur mesure sans l'interrupteur : l'interrupteur est deduit.
  if ((p.vuesCustom || []).length && p.customVues === undefined) p.customVues = true;
}

/**
 * VERSION 1 VERS 2 : LE JEU DE CHAQUE PORTE REMONTE DANS LA LISTE PARTAGEE.
 *
 * CE QUI SE PASSE, ET DANS CET ORDRE :
 *
 *  1. chaque porte qui POSSEDAIT un jeu (`dsMode` autre que `'ref'`) en depose
 *     un dans `brief.jeux`, avec un uid neuf ; son nom est le LIBELLE de la
 *     porte, exactement le nom que la projection ecrivait deja dans
 *     `taki.screens.json` (`portes[].jeu.nom`) ;
 *  2. chaque porte qui REPRENAIT le jeu d'une autre (`dsMode: 'ref'`) voit son
 *     `dsRef` traduit : il designait l'uid d'une PORTE, il designe desormais
 *     l'uid du JEU que cette porte a depose. Deux portes qui partageaient
 *     pointent donc LE MEME jeu, et supprimer l'une n'emporte plus rien ;
 *  3. `demoJeux` suit le meme chemin : ses uid de PORTE deviennent des uid de
 *     JEU, sans quoi la selection du jeu de demo designerait des objets qui
 *     n'existent plus.
 *
 * LE SAC `auto` PASSE TEL QUEL, JAMAIS RECONSTRUIT. `nouveauJeu()` le pose TOUT
 * A VRAI alors qu'une clef absente se lit `false` : c'est le seul defaut du
 * modele qui ne coincide pas avec l'absence, donc le reconstruire retournerait
 * le reglage du fondateur. Meme raison que la regle de `completerSurface`.
 *
 * `dsMode`, `dsOk`, `famMode`, `famOk` et `customDs` TOMBENT, et ils ne laissent
 * rien derriere eux. `famMode` disait « je complete moi-meme » ou « je laisse
 * l'agent decider » : c'est deja ce que dit le sac `auto`, mot pour mot, et
 * c'est pourquoi la rangee qui le saisissait est retiree. Le cas `'moi'` (une
 * valeur hors enum que le gabarit ecrivait) se traite donc comme `'pick'`, sans
 * traitement particulier : aucun des deux ne touche aux champs, qui partent
 * tous dans le jeu.
 *
 * `ds.normaliser` EST LAISSE TOMBER, pas conserve : c'est la consigne. Le
 * reglage « L'agent relit et corrige les champs » etait allume par defaut et
 * disait, sur une seule porte, ce que le bouton global « Relecture IA » fait
 * sur tout le brief. `ds.normNote`, la zone « Consignes en plus » qui ne servait
 * qu'a lui, part avec lui.
 *
 * `ds.manque` SUIT LE JEU, ET C'EST UNE CORRECTION. Il partait avec les deux
 * autres, alors qu'il n'a rien a voir avec la normalisation : l'ecran le demande
 * (« Manque a la famille »), il declare un BLOCAGE SOCLE avant le code, et
 * `prompt.mjs` le transmet. Le jeter aurait efface, a la premiere ouverture d'un
 * ancien brief, la seule phrase qui disait ce que la famille du socle n'a pas.
 *
 * @param {Record<string, any>} b un brief DEJA clone, portes reparees
 * @returns {void}
 */
function sortirLesJeux(b) {
  const jeux = Array.isArray(b.jeux) ? b.jeux : [];
  /** L'uid du jeu depose par chaque porte : la table de traduction de `dsRef`. */
  const parPorte = new Map();

  for (const p of b.portes) {
    if (!p || typeof p !== 'object') continue;
    if (p.dsMode === 'ref') continue;
    /* Une porte canvas qui n'attachait AUCUN jeu (`customDs` absent ou faux) ne
       depose rien : son sac `ds` etait celui du constructeur, jamais rempli, et
       le remonter peuplerait la liste partagee de jeux vides. */
    if (p.custom === true && p.customDs !== true) continue;
    const ds = p.ds && typeof p.ds === 'object' && !Array.isArray(p.ds) ? p.ds : null;
    if (ds === null) continue;

    const jeu = {
      uid: uid(),
      nom: String(p.label ?? '').trim(),
      famille: typeof ds.famille === 'string' ? ds.famille : '',
      type: typeof ds.type === 'string' ? ds.type : '',
      scope: typeof ds.scope === 'string' && ds.scope !== '' ? ds.scope : 'org-wide',
      decoupe: ds.decoupe !== false,
      dsDesc: typeof ds.dsDesc === 'string' ? ds.dsDesc : '',
      manque: typeof ds.manque === 'string' ? ds.manque : '',
      champs: Array.isArray(ds.champs) ? ds.champs : [],
      auto: ds.auto && typeof ds.auto === 'object' && !Array.isArray(ds.auto) ? ds.auto : {},
      instructions: {},
    };

    /* LA PISTE LAISSEE A L'AGENT SUIT SON SUJET. Le texte libre de la
       sous-section « Jeu de donnees » vivait dans le sac de la PORTE, sous la
       clef `jeu` ; il decrit le jeu, il part avec lui. */
    const sac = p.instructions && typeof p.instructions === 'object' ? p.instructions : null;
    if (sac && typeof sac.jeu === 'string' && sac.jeu.trim() !== '') {
      jeu.instructions = { jeu: sac.jeu };
      delete sac.jeu;
    }

    jeux.push(jeu);
    parPorte.set(p.uid, jeu.uid);
  }

  for (const p of b.portes) {
    if (!p || typeof p !== 'object') continue;
    const repris = typeof p.dsRef === 'string' ? p.dsRef : '';
    const vise = p.dsMode === 'ref' ? parPorte.get(repris) : parPorte.get(p.uid);
    /* UN `dsRef` DEJA TRADUIT SE GARDE. Rejouee sur un brief remonte, la porte
       n'a plus de `ds` a lever : `parPorte` est vide pour elle, et effacer son
       `dsRef` la detacherait de son jeu. Meme cause que `demoJeux` juste apres. */
    const dejaBon = repris !== '' && jeux.some((j) => j.uid === repris);
    p.dsRef = typeof vise === 'string' ? vise : dejaBon ? repris : '';
    delete p.customDs;
    delete p.dsMode;
    delete p.dsOk;
    delete p.famMode;
    delete p.famOk;
    delete p.ds;
  }

  /* `demoJeux` portait des uid de PORTE sous un nom qui disait deja « jeu ».
     Une porte qui ne deposait aucun jeu disparait de la liste : elle ne
     designait rien qui existe encore.

     UN UID DEJA TRADUIT SE GARDE TEL QUEL. Sans cette branche, rejouer la
     migration sur un brief deja remonte VIDAIT la liste : les uid y sont alors
     ceux des JEUX, qui ne figurent dans aucune cle de `parPorte`. Le defaut ne
     se voyait pas, parce que le test d'idempotence ecrivait `migrer(migrer(b))`
     et que `migrer` court-circuite sur la version. */
  if (Array.isArray(b.demoJeux)) {
    const existeDeja = new Set((Array.isArray(b.jeux) ? b.jeux : []).map((j) => j && j.uid));
    const vus = new Set();
    /** @type {string[]} */
    const suite = [];
    for (const ancien of b.demoJeux) {
      const traduit = parPorte.get(ancien);
      const garde =
        typeof traduit === 'string' ? traduit : existeDeja.has(ancien) ? ancien : null;
      if (garde === null || vus.has(garde)) continue;
      vus.add(garde);
      suite.push(garde);
    }
    b.demoJeux = suite;
  }

  b.jeux = jeux;
}

/**
 * VERSION 2 VERS 3 : LES QUATRE CLEFS CESSENT DE PORTER DU FRANCAIS.
 *
 * CE QUI SURVIT, ET CE QUI TOMBE. Un mot que le fondateur a TAPE et qui
 * s'affiche survit toujours : le nom d'un filtre, le nom d'un favori. Une
 * PHRASE qui decrivait une reference tombe, parce que la relire serait
 * exactement la faute que ce lot supprime -- on ne devine pas « trie par
 * prochaine maintenance croissante » a partir d'une chaine de caracteres.
 *
 *  1. `filtres: [{ uid, label, garde }]` devient `{ champ: '', autres: [...] }`.
 *     Les rangees deviennent des filtres « autres », c'est-a-dire des TROUS
 *     NOMMES : leur libelle donne leur nom d'appel, leur `garde` reste la prose
 *     que l'agent lira pour ecrire le predicat. Rien n'est perdu, et le
 *     catalogue `parChamp` se choisit ensuite dans le formulaire ;
 *  2. `favoris: [{ uid, label, etat }]` devient `{ uid, label, filtres: [],
 *     tri: [] }`. `etat` melangeait des filtres et un tri dans une seule
 *     phrase : il TOMBE, et le favori garde son nom en attendant qu'on coche
 *     ce qu'il pose ;
 *  3. `tri: 'Nom croissant, puis date'` devient `[]`. Le sens etait dans les
 *     mots (« croissante ») et personne ne le lisait : la chaine tombe.
 *
 * @param {Record<string, any>} b un brief DEJA clone
 * @returns {void}
 */
function structurerLesReferences(b) {
  for (const p of Array.isArray(b.portes) ? b.portes : []) {
    if (!p || typeof p !== 'object') continue;

    if (Array.isArray(p.filtres)) {
      const autres = [];
      for (const f of p.filtres) {
        const label = libelleAncien(f);
        if (label === '') continue;
        autres.push({
          uid: typeof f === 'object' && f !== null && typeof f.uid === 'string' && f.uid !== '' ? f.uid : uid(),
          label,
          garde: typeof f === 'object' && f !== null ? String(f.garde || '') : '',
        });
      }
      p.filtres = { champ: '', autres };
    }

    if (Array.isArray(p.favoris)) {
      const favoris = [];
      for (const f of p.favoris) {
        const label = libelleAncien(f);
        if (label === '') continue;
        favoris.push({
          uid: typeof f === 'object' && f !== null && typeof f.uid === 'string' && f.uid !== '' ? f.uid : uid(),
          label,
          filtres: [],
          tri: [],
        });
      }
      p.favoris = favoris;
    }

    /*
     * LA QUATRIEME CLEF, `actions`. Une action ancienne nomme son bouton `nom`,
     * tout le code neuf lit `label` : sans cette branche, une porte perdait ses
     * actions en silence, `actionsProjetees` ecartant toute action dont le
     * libelle est vide.
     */
    if (Array.isArray(p.actions)) {
      for (const a of p.actions) {
        if (!a || typeof a !== 'object') continue;
        if (typeof a.label === 'string' && a.label.trim() !== '') continue;
        const nom = String(a.nom || '').trim();
        if (nom !== '') a.label = nom;
        delete a.nom;
      }
    }

    if (!Array.isArray(p.tri)) p.tri = [];
  }
}

/**
 * VERSION 3 VERS 4 : UN FAVORI DESIGNE SES FILTRES, il ne les NOMME plus.
 *
 * CE QUE LA VERSION 3 AVAIT LAISSE PASSER. Elle a structure les filtres, les
 * favoris et le tri, puis a stocke dans `favori.filtres` le LIBELLE des cases
 * cochees. Deux degats, tous deux silencieux : retoucher le libelle d'une
 * option faisait disparaitre le favori (la projection ne resolvait plus et
 * ecartait sans rien dire), et un filtre nomme homonyme d'une valeur du
 * catalogue ecrasait cette valeur dans la table de traduction (le favori posait
 * alors le PREDICAT au lieu de l'EGALITE cochee).
 *
 * COMMENT ON TRADUIT, ET DANS QUEL ORDRE. Le catalogue d'abord, les filtres
 * nommes ensuite : c'est l'ordre exact dans lequel l'ecran OFFRE les cases
 * (`filtresOfferts`), donc c'est la case que le fondateur a vue et cochee. Le
 * cas de collision retombe ainsi sur ce qu'il croyait cocher.
 *
 * ET RIEN NE TOMBE. Un libelle qui ne resout NI vers une valeur NI vers un
 * filtre nomme n'est pas jete : il DEVIENT un filtre nomme (un trou nomme, sans
 * garde), et le favori le designe par son uid. C'est le meme geste que la
 * version 3 sur les filtres en chaine nue, et pour la meme raison : une
 * migration qui laisse tomber en silence est exactement le defaut que ce
 * chantier repare.
 *
 * @param {Record<string, any>} b un brief DEJA clone
 * @returns {void}
 */
function referencerLesFavoris(b) {
  const jeux = Array.isArray(b.jeux) ? b.jeux : [];
  for (const p of Array.isArray(b.portes) ? b.portes : []) {
    if (!p || typeof p !== 'object') continue;
    const favoris = Array.isArray(p.favoris) ? p.favoris : [];
    if (favoris.length === 0) continue;

    const sac = p.filtres && typeof p.filtres === 'object' && !Array.isArray(p.filtres) ? p.filtres : {};
    const autres = Array.isArray(sac.autres) ? sac.autres : [];

    /* LA TABLE LIBELLE -> REFERENCE, dans l'ordre des cases offertes : les
       valeurs du champ a choix, PUIS les filtres nommes. Une entree deja posee
       ne se reecrit pas, donc un homonyme ne peut plus ecraser la valeur. */
    const parLibelle = new Map();
    const jeu = jeux.find((j) => j && typeof j === 'object' && String(j.uid || '') === String(p.dsRef || ''));
    const champ = (Array.isArray(jeu && jeu.champs) ? jeu.champs : []).find(
      (c) => c && String(c.uid || '') === String(sac.champ || ''),
    );
    /* LA VALEUR STOCKEE, pas la clef derivee du libelle : `clefsValeurs` peut
       en figer une autre, et c'est elle que la projection resout. */
    for (const valeur of valeursDeChamp(champ)) {
      if (!parLibelle.has(valeur)) parLibelle.set(valeur, refValeur(valeurStockee(champ, valeur)));
    }
    for (const a of autres) {
      const label = String((a && a.label) || '').trim();
      if (label !== '' && !parLibelle.has(label)) parLibelle.set(label, refFiltre(String((a && a.uid) || '')));
    }

    for (const f of favoris) {
      if (!f || typeof f !== 'object' || !Array.isArray(f.filtres)) continue;
      const suite = [];
      for (const brut of f.filtres) {
        const texte = String(brut || '').trim();
        if (texte === '') continue;
        if (estReferenceDeFiltre(texte)) {
          if (!suite.includes(texte)) suite.push(texte);
          continue;
        }
        let ref = parLibelle.get(texte);
        if (ref === undefined) {
          /* PERSONNE NE LE CONNAIT : il devient un filtre NOMME plutot que de
             disparaitre. Le fondateur avait coche un mot, il retrouve ce mot. */
          const neuf = { uid: uid(), label: texte, garde: '' };
          autres.push(neuf);
          ref = refFiltre(neuf.uid);
          parLibelle.set(texte, ref);
        }
        if (!suite.includes(ref)) suite.push(ref);
      }
      f.filtres = suite;
    }

    /* Le catalogue se reecrit parce qu'il a pu GAGNER des filtres nommes ; sa
       clef `champ` ne bouge jamais. */
    p.filtres = { champ: String(sac.champ || ''), autres };
  }
}

/**
 * Le libelle d'un filtre ou d'un favori ANCIEN, quelle qu'ait ete sa forme.
 *
 * DEUX FORMES ONT EXISTE SUR DISQUE, et la seconde n'etait pas prevue : la
 * forme objet `{ uid, label, garde }`, et la forme CHAINE, ou le brief posait
 * `filtres: ['Formule', 'Statut']`. Ne lire que `.label` faisait tomber la
 * seconde a la migration, sans rien dire : c'est la perte de donnees que ce
 * garde-fou existe pour empecher.
 *
 * @param {unknown} f
 * @returns {string}
 */
function libelleAncien(f) {
  if (typeof f === 'string') return f.trim();
  if (f && typeof f === 'object') return String(f.label || '').trim();
  return '';
}

/**
 * Les familles du catalogue portaient un nom que le SDK n'expose PAS.
 *
 * LE DEGAT, mesure : le SDK rend `sdk.bookings`, `sdk.places`, `sdk.tasks`, et
 * le catalogue du Studio proposait `booking`, `place`, `task`. Sur 57 familles
 * offertes, DEUX nommaient une propriete reelle. Une app nee des 55 autres
 * appelait un `sdk.<famille>` valant `undefined` : elle compilait, et cassait a
 * la premiere lecture.
 *
 * LE GARDE-FOU NE VOYAIT RIEN parce qu'il comparait le catalogue aux FABRIQUES
 * (`createBookingRepository`, au singulier) et non a la surface reelle de
 * `createTakiSdk`. Il compare desormais la bonne chose, et refuse une famille
 * qui n'a pas sa propriete (verifie en la sabotant).
 *
 * @type {Record<string, string>}
 */
const FAMILLES_RENOMMEES = {
  "absence": 'absences',
  "accessGrant": 'accessGrants',
  "agreement": 'agreements',
  "attendance": 'attendances',
  "availability": 'availabilities',
  "booking": 'bookings',
  "campaign": 'campaigns',
  "careRecord": 'careRecords',
  "composition": 'compositions',
  "consent": 'consents',
  "credential": 'credentials',
  "credit": 'credits',
  "custody": 'custodies',
  "document": 'documents',
  "donation": 'donations',
  "event": 'events',
  "expense": 'expenses',
  "form": 'forms',
  "group": 'groups',
  "incident": 'incidents',
  "inspection": 'inspections',
  "invoice": 'invoices',
  "listing": 'listings',
  "loan": 'loans',
  "lot": 'lots',
  "measurement": 'measurements',
  "message": 'messages',
  "opportunity": 'opportunities',
  "order": 'orders',
  "payment": 'payments',
  "place": 'places',
  "policy": 'policies',
  "priceList": 'priceLists',
  "product": 'products',
  "project": 'projects',
  "registerSession": 'registerSessions',
  "registration": 'registrations',
  "relationship": 'relationships',
  "resource": 'resources',
  "review": 'reviews',
  "route": 'routes',
  "shift": 'shifts',
  "shipment": 'shipments',
  "stock": 'stocks',
  "stockMovement": 'stockMovements',
  "subscription": 'subscriptions',
  "task": 'tasks',
  "ticket": 'tickets',
  "timesheet": 'timesheets',
  "voucher": 'vouchers',
  "warranty": 'warranties',
  "workOrder": 'workOrders',
  "worker": 'workers',
};

/**
 * Un jeu deja ecrit garde le nom de famille d'avant : on le remonte.
 *
 * @param {Record<string, any>} b un brief DEJA clone
 * @returns {void}
 */
function renommerLesFamilles(b) {
  const remonter = (v) => {
    const f = String(v || '');
    if (f === '') return f;
    /* Un trou nomme (`@a-decider/<porte>`) n'est pas une famille : on n'y touche pas. */
    if (estFamilleADecider(f)) return f;
    return Object.prototype.hasOwnProperty.call(FAMILLES_RENOMMEES, f) ? FAMILLES_RENOMMEES[f] : f;
  };

  for (const j of Array.isArray(b.jeux) ? b.jeux : []) {
    if (j && typeof j === 'object') j.famille = remonter(j.famille);
  }
  /* Les jeux d'avant la forme 2 vivaient dans la porte : la chaine de migration
     les a deja remontes quand on arrive ici, mais un brief bricole a la main
     peut encore en porter. */
  for (const p of Array.isArray(b.portes) ? b.portes : []) {
    if (p && p.ds && typeof p.ds === 'object') p.ds.famille = remonter(p.ds.famille);
  }
  if (Array.isArray(b.entities)) b.entities = b.entities.map(remonter);
}

/**
 * La valeur comparee d'un compteur etait le LIBELLE de l'option, pas sa valeur.
 *
 * MEME DEFAUT QUE LES FAVORIS, au meme endroit du modele et trouve juste apres :
 * `statsProjetees` cherchait l'option par `o.label` et ECARTAIT le compteur en
 * silence quand le libelle ne resolvait plus. Retoucher « A confirmer » en
 * « A confirmer (client) » faisait disparaitre la stat, sans un mot.
 *
 * @param {Record<string, any>} b un brief DEJA clone
 * @returns {void}
 */
function referencerLesStats(b) {
  const jeux = new Map(
    (Array.isArray(b.jeux) ? b.jeux : []).map((j) => [j && j.uid, j]),
  );

  for (const p of Array.isArray(b.portes) ? b.portes : []) {
    if (!p || !Array.isArray(p.stats)) continue;
    const jeu = jeux.get(p.dsRef);
    const champs = jeu && Array.isArray(jeu.champs) ? jeu.champs : [];

    for (const st of p.stats) {
      if (!st || typeof st !== 'object') continue;
      const brut = String(st.valeur || '').trim();
      if (brut === '') continue;

      /*
       * `stat.champ` PORTE UN UID, PAS UNE CLEF. C'est ainsi que `champDeStat`
       * le resout (`derive.mjs`, `x.uid === stat.champ`). Cette migration
       * comparait `clefDeChamp(nom)` a cet uid : la comparaison n'aboutissait
       * JAMAIS, aucune stat n'etait remontee, et le commentaire de la fonction
       * affirmait un travail que le code ne faisait pas. On accepte les deux
       * formes, l'uid d'abord, parce qu'un brief bricole a la main peut porter
       * la clef.
       */
      const champ =
        champs.find((c) => c && c.uid === st.champ) ||
        champs.find((c) => c && clefDeChamp(String(c.nom || '')) === st.champ);
      const libelles = champ ? valeursDeChamp(champ) : [];
      /* Deja une valeur : on n'y touche pas, et c'est ce qui rend la migration
         idempotente sur une stat qu'un round anterieur a deja remontee. La
         valeur est la STOCKEE (`valeurStockee`) : `clefsValeurs` peut en figer
         une autre que la clef derivee du libelle. */
      if (libelles.some((l) => valeurStockee(champ, l) === brut)) continue;
      const trouve = libelles.find((l) => l === brut);
      if (trouve !== undefined) st.valeur = valeurStockee(champ, trouve);
    }
  }
}

/**
 * UNE LISTE DE VUES SUR MESURE QUI SURVIT A SON DRAPEAU (consigne 14).
 *
 * CE QUE LE FONDATEUR A VU : la porte « Voitures » portait un `vuesCustom` avec
 * une rangee VIDE alors que « vues sur mesure » etait DECOCHE. Rien ne la
 * lisait (`aDesViewCanvas` exige un nom non vide, `customVues` commande le
 * reste), rien ne la nettoyait, et la relecture ne savait pas supprimer une
 * ligne. Elle ne genait pas le generateur : elle genait le fondateur, qui
 * voyait une rangee inutile qu'il ne pouvait pas retirer.
 *
 * CE QU'ON NETTOIE, ET RIEN DE PLUS : la liste d'une porte dont `customVues`
 * n'est PAS coche. Une porte qui coche garde ses rangees, meme vides : ce sont
 * ses rangees a remplir. Idempotent par construction (une liste deja vide ne
 * bouge pas).
 *
 * @param {Record<string, any>} b un brief DEJA clone
 * @returns {void}
 */
function viderLesVuesCustom(b) {
  for (const p of Array.isArray(b.portes) ? b.portes : []) {
    if (!p || typeof p !== 'object') continue;
    if (p.customVues === true) continue;
    if (Array.isArray(p.vuesCustom) && p.vuesCustom.length > 0) p.vuesCustom = [];
  }
}

/**
 * VERSION 7 VERS 8 : LE BOOLEEN `wide` D'UNE CARTE DE FICHE DEVIENT UNE
 * DISPOSITION NOMMEE.
 *
 * CE QUE `wide` CACHAIT. Il ne disait que « la carte prend la rangee », alors
 * que le socle rendait DEUX choses derriere ce seul mot : une carte large a UN
 * champ etale ce champ sur toute la largeur (la carte Notes), une carte large a
 * PLUSIEURS champs pose son TITRE sur la rangee et repartit ses champs sur les
 * deux colonnes. La traduction est donc dictee par le rendu d'AVANT, pas par un
 * gout : `wide` plus un seul champ vaut `pleine`, `wide` plus plusieurs champs
 * vaut `titre-pleine`. Aucune fiche ne change d'allure a la migration, et c'est
 * la seule facon de ne pas repeindre les apps deja ecrites.
 *
 * UNE CARTE LIEE N'EN RECOIT PAS : elle monte une vue du socle sur les fiches
 * d'une autre porte, elle prend toujours la rangee entiere, et le schema
 * (`appScreenLinkedCardSchema`, `.strict()`) refuse la clef.
 *
 * IDEMPOTENT, ET PAS SEULEMENT PARCE QUE `migrer` COURT-CIRCUITE : une carte qui
 * porte DEJA sa disposition la garde. Sans ce garde, un second passage
 * retomberait sur « pas de `wide`, donc demi » et repeindrait en demi une carte
 * pleine. C'est aussi la regle de `migrer` : aucune valeur presente n'est
 * jamais ecrasee.
 *
 * @param {Record<string, any>} b un brief DEJA clone
 * @returns {void}
 */
function nommerLaDisposition(b) {
  for (const p of Array.isArray(b.portes) ? b.portes : []) {
    if (!p || typeof p !== 'object') continue;
    const fiche = (p.vb || {})['core.fiche'];
    if (!fiche || !Array.isArray(fiche.cards)) continue;
    for (const c of fiche.cards) {
      if (!c || typeof c !== 'object') continue;
      /* NI LA CARTE LIEE NI CELLE DES FICHIERS n'ont de disposition : le schema
         refuse la clef sur l'une comme sur l'autre. */
      if (c.liee !== undefined || c.fichiers === true) continue;
      if (c.disposition === undefined) {
        const clefs = Array.isArray(c.keys) ? c.keys.length : 0;
        c.disposition = c.wide === true ? (clefs > 1 ? 'titre-pleine' : 'pleine') : 'demi';
      }
      delete c.wide;
    }
  }
}

/**
 * VERSION 8 VERS 9 : UNE VALEUR FIGEE SE COCHE TELLE QU'ELLE EST STOCKEE.
 *
 * CE QUE LE STUDIO ECRIVAIT JUSQU'A LA FORME 8. La case offerte a un favori et
 * la valeur d'une stat au formulaire derivaient la valeur d'une option de son
 * LIBELLE (`clefDeChamp`), alors que le fichier de champs ecrit la valeur
 * STOCKEE (`valeurStockee`). Les deux coincident tant que `clefsValeurs` ne
 * fige rien. Des qu'il fige « Fournisseur » en `supplier`, un brief sauve
 * portait `valeur:fournisseur`, ou une stat qui compte `fournisseur`, et la
 * projection l'ecartait sans rien dire. Vu le 14/09/2026 a la naissance de
 * l'Annuaire.
 *
 * CE QU'ON TRADUIT, ET SEULEMENT CA. Une reference `valeur:<x>` d'un favori, la
 * valeur d'une stat ou la valeur par defaut d'une liste de choix, qui ne RESOUT
 * PAS vers une valeur stockee de son champ, et dont EXACTEMENT UN libelle du
 * champ derive `x` : ce libelle est la case que le fondateur a cochee, elle
 * prend sa valeur stockee. La valeur par defaut, tapee a la main jusqu'ici,
 * accepte aussi le libelle exact. Aucun libelle, ou deux qui derivent le meme
 * `x`, et on ne devine pas : le validateur le dit.
 *
 * IDEMPOTENT PAR CONSTRUCTION : une valeur qui resout n'est jamais touchee, et
 * une liste ou rien ne se traduit reste l'objet qu'elle etait. `filtre:<uid>`
 * n'est jamais lu.
 *
 * @param {Record<string, any>} b un brief DEJA clone
 * @returns {void}
 */
function retrouverLesValeursFigees(b) {
  const jeux = Array.isArray(b.jeux) ? b.jeux : [];

  /* LA VALEUR STOCKEE QUE `brut` VOULAIT DIRE, ou `null` quand il ne faut pas
     y toucher (il resout deja, ou rien ne le designe sans ambiguite). */
  const valeurVoulue = (champ, brut) => {
    const libelles = valeursDeChamp(champ);
    if (libelles.some((l) => valeurStockee(champ, l) === brut)) return null;
    const derives = libelles.filter((l) => clefDeChamp(l) === brut);
    return derives.length === 1 ? valeurStockee(champ, derives[0]) : null;
  };

  for (const p of Array.isArray(b.portes) ? b.portes : []) {
    if (!p || typeof p !== 'object') continue;
    const jeu = jeux.find((j) => j && typeof j === 'object' && String(j.uid || '') === String(p.dsRef || ''));
    const champs = Array.isArray(jeu && jeu.champs) ? jeu.champs : [];
    const champDUid = (id) => champs.find((c) => c && String(c.uid || '') === String(id || ''));

    const sac = p.filtres && typeof p.filtres === 'object' && !Array.isArray(p.filtres) ? p.filtres : {};
    const catalogue = champDUid(sac.champ);
    for (const f of catalogue !== undefined && Array.isArray(p.favoris) ? p.favoris : []) {
      if (!f || typeof f !== 'object' || !Array.isArray(f.filtres)) continue;
      let traduit = false;
      const suite = [];
      for (const coche of f.filtres) {
        const texte = String(coche || '');
        const voulue = texte.startsWith(REF_VALEUR) ? valeurVoulue(catalogue, texte.slice(REF_VALEUR.length)) : null;
        if (voulue !== null) traduit = true;
        const pose = voulue === null ? coche : refValeur(voulue);
        if (!suite.includes(pose)) suite.push(pose);
      }
      if (traduit) f.filtres = suite;
    }

    for (const st of Array.isArray(p.stats) ? p.stats : []) {
      if (!st || typeof st !== 'object') continue;
      const brut = String(st.valeur || '').trim();
      const champ = brut === '' ? undefined : champDUid(st.champ);
      if (champ === undefined) continue;
      const voulue = valeurVoulue(champ, brut);
      if (voulue !== null) st.valeur = voulue;
    }

    /* LA VALEUR PAR DEFAUT D'UNE LISTE DE CHOIX se TAPAIT, sans rien qui dise
       la valeur stockee : elle porte donc souvent le LIBELLE (« Fournisseur »),
       ou sa clef derivee. Le libelle exact d'abord, la clef derivee ensuite,
       et toujours sans deviner. */
    const ajout = p.ajout && typeof p.ajout === 'object' ? p.ajout : {};
    for (const d of Array.isArray(ajout.defauts) ? ajout.defauts : []) {
      if (!d || typeof d !== 'object') continue;
      const brut = String(d.valeur ?? '').trim();
      const champ = brut === '' ? undefined : champDUid(d.uid);
      if (champ === undefined || !NATURES_A_CHOIX.includes(champ.nature)) continue;
      const libelles = valeursDeChamp(champ);
      if (libelles.some((l) => valeurStockee(champ, l) === brut)) continue;
      const voulue = libelles.includes(brut) ? valeurStockee(champ, brut) : valeurVoulue(champ, brut);
      if (voulue !== null) d.valeur = voulue;
    }
  }
}

/**
 * Remonte un brief d'une version anterieure jusqu'a `VERSION_MODELE`.
 *
 * IDEMPOTENT : `migrer(migrer(b))` rend le meme brief que `migrer(b)`. Le brief
 * d'entree n'est JAMAIS modifie, on rend toujours un objet neuf.
 *
 * CINQ CAS AUJOURD'HUI. Un brief SANS champ `version` vient de V1 : on lui
 * applique les rustines de `load()`. Un brief en `version: 1` (ou de V1, donc
 * les deux) voit ses jeux de donnees SORTIR de ses portes (`sortirLesJeux`) :
 * c'est la seule migration qui deplace de la donnee au lieu d'en completer. Un
 * brief anterieur a la version 3 voit ses filtres, ses favoris et son tri
 * changer de FORME (`structurerLesReferences`) ; un brief anterieur a la
 * version 4 voit les filtres coches de ses favoris passer du LIBELLE a la
 * REFERENCE (`referencerLesFavoris`) ; un brief anterieur a la version 7 voit
 * se VIDER la liste de vues sur mesure des portes qui ne cochent pas
 * `customVues` (`viderLesVuesCustom`) ; un brief anterieur a la version 8 voit
 * le booleen `wide` de chaque carte de fiche devenir une DISPOSITION nommee
 * (`nommerLaDisposition`) ; un brief anterieur a la version 9 voit une valeur
 * figee par `clefsValeurs`, cochee sous sa clef derivee du libelle, reprendre
 * sa valeur STOCKEE (`retrouverLesValeursFigees`).
 * Dans tous les cas on complete ensuite les cles absentes avec les defauts de
 * `nouveauBrief()` et `nouvellePorte()`, et on estampille. Aucune valeur
 * presente n'est jamais ecrasee.
 *
 * Un brief d'une version PLUS RECENTE que la notre est rendu tel quel : on ne sait
 * pas redescendre, et le casser serait pire que le laisser passer.
 *
 * CE QU'IL NE FAIT PAS, ET QUI EST VOULU : un brief DEJA en `VERSION_MODELE` sort
 * d'ici sans avoir gagne une seule clef. Une clef facultative ajoutee au modele
 * depuis (`canvasKeeps`, `appCanvasKeeps`, `canvasGrantedOn`, les drapeaux de
 * section, `demoTousJeux`...) reste donc ABSENTE d'un brief sauve avant elle.
 * C'est la raison pour laquelle chaque nouvelle clef se pose a sa valeur NEUTRE
 * et se lit avec `=== true` / `=== false` / `Array.isArray`, jamais avec un `!`
 * nu : la valeur du constructeur ne dit RIEN de ce que vaut une clef absente,
 * c'est la LECTURE qui le decide. Le piege a deja ete paye deux fois sur ce lot
 * (`demoTousJeux`, puis les drapeaux de section).
 *
 * @param {unknown} brief
 * @returns {Brief}
 */
export function migrer(brief) {
  // Rien d'exploitable : on repart d'un brief neuf, comme le `load() || blank()` de V1.
  if (!brief || typeof brief !== 'object' || Array.isArray(brief)) return nouveauBrief();

  const b = /** @type {Record<string, any>} */ (cloner(brief));
  oublierLesReglages(b);

  // Deja a jour (ou plus recent) : rien a faire, sauf figer les clefs des champs
  // qui ne le sont pas encore (regle du 4 septembre), et fondre les natures
  // doublons, deux pas qui restent idempotents.
  if (typeof b.version === 'number' && b.version >= VERSION_MODELE) {
    fondreLesNaturesDoublons(b);
    return /** @type {any} */ (figerLesClefs(b));
  }

  /* La FORME d'ou l'on part. Un brief sans `version` vient de V1 (le Launcher) ;
     un brief en `version: 1` vient du Studio d'avant la sortie des jeux. */
  const forme = typeof b.version === 'number' ? b.version : 0;

  b.portes = Array.isArray(b.portes) ? b.portes : [];
  for (const p of b.portes) {
    if (!p || typeof p !== 'object') continue;
    reparerPorteV1(p);
  }
  /* LES JEUX SORTENT DES PORTES, et ca doit se faire APRES les rustines de V1
     (l'une d'elles corrige `p.ds.champs`) et AVANT la completion (les defauts
     d'une porte ne portent plus de sac `ds` a completer). */
  if (forme < 2) sortirLesJeux(b);
  /* LES QUATRE CLEFS SE STRUCTURENT APRES la sortie des jeux (elle ne les
     touche pas) et AVANT la completion : `completerSurface` ne pose que les
     clefs ABSENTES, donc une clef laissee a l'ancienne forme ne serait jamais
     corrigee. */
  if (forme < 3) structurerLesReferences(b);
  /* LES FAVORIS PASSENT AUX REFERENCES APRES la structuration (c'est elle qui
     cree la clef `filtres` sous sa forme objet) et AVANT la completion, pour la
     meme raison qu'elle : `completerSurface` ne pose que les clefs ABSENTES. */
  if (forme < 4) referencerLesFavoris(b);
  if (forme < 5) renommerLesFamilles(b);
  if (forme < 6) referencerLesStats(b);
  /* LA LISTE DE VUES SUR MESURE D'UNE PORTE QUI NE LES COCHE PAS SE VIDE. Elle
     passe AVANT la completion, comme les cinq precedentes : `completerSurface`
     ne pose que les clefs ABSENTES, donc une liste presente ne serait jamais
     revue. */
  if (forme < 7) viderLesVuesCustom(b);
  /* LE BOOLEEN `wide` D'UNE CARTE DE FICHE DEVIENT UNE DISPOSITION NOMMEE. Elle
     passe APRES `reparerPorteV1` (c'est cette rustine qui traduit encore
     `pos: 'pleine largeur'` en `wide: true`, et un `wide` pose apres nous ne
     serait jamais relu) et AVANT la completion, comme les six precedentes. */
  if (forme < 8) nommerLaDisposition(b);
  /* UNE VALEUR FIGEE SE COCHE TELLE QU'ELLE EST STOCKEE. Elle passe APRES
     `referencerLesFavoris` et `referencerLesStats` (elles ecrivent deja la
     valeur stockee, et on ne traduit que ce qui ne resout pas) et AVANT la
     completion, comme les sept precedentes. */
  if (forme < 9) retrouverLesValeursFigees(b);
  for (const p of b.portes) {
    if (!p || typeof p !== 'object') continue;
    // Un jeu de defauts NEUF par porte : deux portes ne doivent jamais partager un sac.
    completerSurface(p, nouvellePorte());
  }
  completerSurface(b, nouveauBrief());
  fondreLesNaturesDoublons(b);

  // `version` en tete : c'est la premiere chose qu'on lit dans le fichier.
  delete b.version;
  return /** @type {any} */ (figerLesClefs({ version: VERSION_MODELE, ...b }));
}


/**
 * Un brief COMPLET, pret pour la generation ET pour l'affichage.
 *
 * POURQUOI CE N'EST PAS `migrer` QUI SUFFIT. `migrer` remonte un brief d'une
 * VERSION anterieure, et son contrat le dit : un brief deja a la version
 * courante en ressort sans avoir gagne une seule clef. C'est exactement le cas
 * du brief qui nous arrive : le Studio l'ecrit a la version courante, puis un
 * humain ou un agent le relit dans son editeur, ou il voyage d'une session a
 * l'autre. Il repart avec le bon numero de version et, peut-etre, une clef en
 * moins.
 *
 * CE QUE LE GENERATEUR NE PARDONNE PAS. `prompt.mjs` deref sans filet
 * (`jeu.champs.filter`, `p.vues.includes`, `p.ajout.defauts`) : une clef absente
 * n'y donne pas un prompt incomplet mais un « Cannot read properties of
 * undefined », qui n'apprend rien et accuse le moteur. Les defauts viennent de
 * `nouvellePorte()`, jamais d'une copie : si la forme d'une porte change, cette
 * fonction suit toute seule.
 *
 * ON COMPLETE, ON NE CORRIGE PAS. Une clef presente n'est jamais touchee, meme
 * absurde : ce n'est pas le role de la creation de reparer un brief, seulement
 * de ne pas tomber sur ce qu'un editeur de texte a pu enlever.
 *
 * ELLE VIT ICI, ET PLUS DANS LE MOTEUR. Un projet IMPORTE est par definition un
 * fichier retouche a la main, et c'est le NAVIGATEUR qui l'ouvre : le moteur ne
 * le voit jamais. La recopier cote ecran aurait fait deux fonctions du meme nom
 * qui divergeraient ; ce module tourne dans les deux mondes, c'est sa place.
 * Le moteur l'importe d'ici, comme il importe deja `migrer` et `nouvellePorte`.
 *
 * @param {unknown} brief
 * @returns {any}
 */
export function briefComplet(brief) {
  if (!brief || typeof brief !== 'object') return brief;

  const complet = migrer(brief);
  const defauts = nouvellePorte();

  for (const porte of Array.isArray(complet.portes) ? complet.portes : []) {
    if (!porte || typeof porte !== 'object') continue;
    for (const [clef, valeur] of Object.entries(defauts)) {
      if (porte[clef] === undefined) porte[clef] = cloner(valeur);
    }
  }

  /* LES JEUX SE COMPLETENT COMME LES PORTES, et pour la meme raison : le
     generateur les deref sans filet (`jeu.champs.filter`, `jeu.famille`). Un
     jeu de defauts NEUF par jeu, sinon deux jeux partageraient un sac -- et
     surtout le meme `uid`, ce qui ferait pointer toutes les portes sur un seul.
     Le sac `auto`, lui, ne se complete PAS en profondeur : voir
     `completerSurface`. */
  if (!Array.isArray(complet.jeux)) complet.jeux = [];
  for (const jeu of complet.jeux) {
    if (!jeu || typeof jeu !== 'object') continue;
    for (const [clef, valeur] of Object.entries(nouveauJeu())) {
      if (jeu[clef] === undefined) jeu[clef] = valeur;
    }
  }

  return complet;
}

/**
 * LE FAIT « CETTE APP A ETE CONSTRUITE », ECRIT DANS LE PROJET.
 *
 * CE QUE LE FONDATEUR A VU (03/09) : en cliquant « Construire avec le script »,
 * l'ecran « colle ceci a ton agent » apparaissait un demi-seconde, puis l'ecran
 * de chargement clignotait, puis retour a la fiche de construction. Il a du
 * filmer au ralenti pour le voir. LA CAUSE : construire ecrit `registry.tsx`,
 * donc Vite recharge la page entiere, donc tout etat d'ecran meurt, le texte a
 * coller avec. Un etat React ne survit pas a sa propre construction.
 *
 * Le fait se pose donc ICI, dans le fichier du projet, cote pont, AVANT de
 * repondre au navigateur : un rechargement le retrouve. `texte` est celui que
 * le moteur a rendu (`initText`), le fondateur le copie quand il veut.
 *
 * @param {object} brief
 * @param {{ appId: string, le: string, texte: string }} fait
 * @returns {object} un NOUVEAU brief, l'entree n'est pas mutee
 */
export function marquerConstruite(brief, fait) {
  return {
    ...brief,
    construite: {
      appId: String(fait.appId || ''),
      le: String(fait.le || ''),
      texte: String(fait.texte || ''),
    },
  };
}
